/**
 * AUTOGENERATED FILE - DO NOT MODIFY DIRECTLY
 *  Run `npm run generate-css-properties` to regenerate the file directly.
 *  The script can be found in: packages/systems/style/properties/scripts/generate.ts
 *
 *  This is generated from https://github.com/mdn/data/blob/main/css/properties.json
 */

import type {
  PropertyOrigin,
  StyleProperty,
  KeywordValue,
  StyleStore$ValueType,
} from '@packages/systems/style-legacy/values';

import type {CssPropertyDetail_Internal} from './types';

type StylePanelProperty<V> = {
  /** The value of the style property */
  value: V;
  origin: PropertyOrigin;
};

/**
 * Opt-in vendor prefixed properties that are supported in Webflow.
 */
export const VENDOR_PROPERTIES_SUPPORTED = new Set<string>([
  '-webkit-text-fill-color',
  '-webkit-text-stroke-width',
  '-webkit-text-stroke-color',
]);

/**
 * Known properties that aren't working with custom properties yet. Mostly
 * used for Cypress testing purposes, but also as a record of what doesn't work
 * which we can iterate and work through individually.
 */
export const KNOWN_NOT_WORKING_CUSTOM_PROPERTY = new Set<string>([
  'background',
  'background-attachment',
  'background-image',
  'background-position',
  'background-repeat',
  'background-size',
  'font-variation-settings',
  'transition',
  'transition-delay',
  'transition-duration',
  'transition-property',
  'transition-timing-function',
]);

export const CSS_UNITS = [
  'cap',
  'ch',
  'cm',
  'deg',
  'dpcm',
  'dpi',
  'dppx',
  'em',
  'ex',
  'fr',
  'grad',
  'Hz',
  'ic',
  'in',
  'kHz',
  'mm',
  'ms',
  'pc',
  'pt',
  'px',
  'Q',
  'rad',
  'rem',
  's',
  'turn',
  'vh',
  'vmax',
  'vmin',
  'vw',
  'x',
] as const;

/**
 * Union of all css properties supported in Webflow.
 */
export type WebflowCssPropertyFull =
  | '-webkit-text-fill-color'
  | '-webkit-text-stroke-color'
  | '-webkit-text-stroke-width'
  | 'accent-color'
  | 'align-content'
  | 'align-items'
  | 'align-self'
  | 'align-tracks'
  | 'all'
  | 'anchor-name'
  | 'anchor-scope'
  | 'animation'
  | 'animation-composition'
  | 'animation-delay'
  | 'animation-direction'
  | 'animation-duration'
  | 'animation-fill-mode'
  | 'animation-iteration-count'
  | 'animation-name'
  | 'animation-play-state'
  | 'animation-range'
  | 'animation-range-end'
  | 'animation-range-start'
  | 'animation-timing-function'
  | 'animation-timeline'
  | 'appearance'
  | 'aspect-ratio'
  | 'azimuth'
  | 'backdrop-filter'
  | 'backface-visibility'
  | 'background'
  | 'background-attachment'
  | 'background-blend-mode'
  | 'background-clip'
  | 'background-color'
  | 'background-image'
  | 'background-origin'
  | 'background-position'
  | 'background-position-x'
  | 'background-position-y'
  | 'background-repeat'
  | 'background-size'
  | 'block-size'
  | 'border'
  | 'border-block'
  | 'border-block-color'
  | 'border-block-style'
  | 'border-block-width'
  | 'border-block-end'
  | 'border-block-end-color'
  | 'border-block-end-style'
  | 'border-block-end-width'
  | 'border-block-start'
  | 'border-block-start-color'
  | 'border-block-start-style'
  | 'border-block-start-width'
  | 'border-bottom'
  | 'border-bottom-color'
  | 'border-bottom-left-radius'
  | 'border-bottom-right-radius'
  | 'border-bottom-style'
  | 'border-bottom-width'
  | 'border-collapse'
  | 'border-color'
  | 'border-end-end-radius'
  | 'border-end-start-radius'
  | 'border-image'
  | 'border-image-outset'
  | 'border-image-repeat'
  | 'border-image-slice'
  | 'border-image-source'
  | 'border-image-width'
  | 'border-inline'
  | 'border-inline-end'
  | 'border-inline-color'
  | 'border-inline-style'
  | 'border-inline-width'
  | 'border-inline-end-color'
  | 'border-inline-end-style'
  | 'border-inline-end-width'
  | 'border-inline-start'
  | 'border-inline-start-color'
  | 'border-inline-start-style'
  | 'border-inline-start-width'
  | 'border-left'
  | 'border-left-color'
  | 'border-left-style'
  | 'border-left-width'
  | 'border-radius'
  | 'border-right'
  | 'border-right-color'
  | 'border-right-style'
  | 'border-right-width'
  | 'border-spacing'
  | 'border-start-end-radius'
  | 'border-start-start-radius'
  | 'border-style'
  | 'border-top'
  | 'border-top-color'
  | 'border-top-left-radius'
  | 'border-top-right-radius'
  | 'border-top-style'
  | 'border-top-width'
  | 'border-width'
  | 'bottom'
  | 'box-align'
  | 'box-decoration-break'
  | 'box-direction'
  | 'box-flex'
  | 'box-flex-group'
  | 'box-lines'
  | 'box-ordinal-group'
  | 'box-orient'
  | 'box-pack'
  | 'box-shadow'
  | 'box-sizing'
  | 'break-after'
  | 'break-before'
  | 'break-inside'
  | 'caption-side'
  | 'caret'
  | 'caret-color'
  | 'caret-shape'
  | 'clear'
  | 'clip'
  | 'clip-path'
  | 'clip-rule'
  | 'color'
  | 'color-interpolation-filters'
  | 'color-scheme'
  | 'column-count'
  | 'column-fill'
  | 'column-gap'
  | 'column-rule'
  | 'column-rule-color'
  | 'column-rule-style'
  | 'column-rule-width'
  | 'column-span'
  | 'column-width'
  | 'columns'
  | 'contain'
  | 'contain-intrinsic-size'
  | 'contain-intrinsic-block-size'
  | 'contain-intrinsic-height'
  | 'contain-intrinsic-inline-size'
  | 'contain-intrinsic-width'
  | 'container'
  | 'container-name'
  | 'container-type'
  | 'content'
  | 'content-visibility'
  | 'counter-increment'
  | 'counter-reset'
  | 'counter-set'
  | 'cursor'
  | 'd'
  | 'cx'
  | 'cy'
  | 'direction'
  | 'display'
  | 'dominant-baseline'
  | 'empty-cells'
  | 'field-sizing'
  | 'fill'
  | 'fill-opacity'
  | 'fill-rule'
  | 'filter'
  | 'flex'
  | 'flex-basis'
  | 'flex-direction'
  | 'flex-flow'
  | 'flex-grow'
  | 'flex-shrink'
  | 'flex-wrap'
  | 'float'
  | 'font'
  | 'font-family'
  | 'font-feature-settings'
  | 'font-kerning'
  | 'font-language-override'
  | 'font-optical-sizing'
  | 'font-palette'
  | 'font-variation-settings'
  | 'font-size'
  | 'font-size-adjust'
  | 'font-smooth'
  | 'font-stretch'
  | 'font-style'
  | 'font-synthesis'
  | 'font-synthesis-position'
  | 'font-synthesis-small-caps'
  | 'font-synthesis-style'
  | 'font-synthesis-weight'
  | 'font-variant'
  | 'font-variant-alternates'
  | 'font-variant-caps'
  | 'font-variant-east-asian'
  | 'font-variant-ligatures'
  | 'font-variant-numeric'
  | 'font-variant-position'
  | 'font-weight'
  | 'forced-color-adjust'
  | 'gap'
  | 'grid'
  | 'grid-area'
  | 'grid-auto-columns'
  | 'grid-auto-flow'
  | 'grid-auto-rows'
  | 'grid-column'
  | 'grid-column-end'
  | 'grid-column-gap'
  | 'grid-column-start'
  | 'grid-gap'
  | 'grid-row'
  | 'grid-row-end'
  | 'grid-row-gap'
  | 'grid-row-start'
  | 'grid-template'
  | 'grid-template-areas'
  | 'grid-template-columns'
  | 'grid-template-rows'
  | 'hanging-punctuation'
  | 'height'
  | 'hyphenate-character'
  | 'hyphenate-limit-chars'
  | 'hyphens'
  | 'image-orientation'
  | 'image-rendering'
  | 'image-resolution'
  | 'ime-mode'
  | 'initial-letter'
  | 'initial-letter-align'
  | 'inline-size'
  | 'input-security'
  | 'inset'
  | 'inset-block'
  | 'inset-block-end'
  | 'inset-block-start'
  | 'inset-inline'
  | 'inset-inline-end'
  | 'inset-inline-start'
  | 'interpolate-size'
  | 'isolation'
  | 'justify-content'
  | 'justify-items'
  | 'justify-self'
  | 'justify-tracks'
  | 'left'
  | 'letter-spacing'
  | 'line-break'
  | 'line-height'
  | 'line-height-step'
  | 'list-style'
  | 'list-style-image'
  | 'list-style-position'
  | 'list-style-type'
  | 'margin'
  | 'margin-block'
  | 'margin-block-end'
  | 'margin-block-start'
  | 'margin-bottom'
  | 'margin-inline'
  | 'margin-inline-end'
  | 'margin-inline-start'
  | 'margin-left'
  | 'margin-right'
  | 'margin-top'
  | 'margin-trim'
  | 'marker'
  | 'marker-end'
  | 'marker-mid'
  | 'marker-start'
  | 'mask'
  | 'mask-border'
  | 'mask-border-mode'
  | 'mask-border-outset'
  | 'mask-border-repeat'
  | 'mask-border-slice'
  | 'mask-border-source'
  | 'mask-border-width'
  | 'mask-clip'
  | 'mask-composite'
  | 'mask-image'
  | 'mask-mode'
  | 'mask-origin'
  | 'mask-position'
  | 'mask-repeat'
  | 'mask-size'
  | 'mask-type'
  | 'masonry-auto-flow'
  | 'math-depth'
  | 'math-shift'
  | 'math-style'
  | 'max-block-size'
  | 'max-height'
  | 'max-inline-size'
  | 'max-lines'
  | 'max-width'
  | 'min-block-size'
  | 'min-height'
  | 'min-inline-size'
  | 'min-width'
  | 'mix-blend-mode'
  | 'object-fit'
  | 'object-position'
  | 'offset'
  | 'offset-anchor'
  | 'offset-distance'
  | 'offset-path'
  | 'offset-position'
  | 'offset-rotate'
  | 'opacity'
  | 'order'
  | 'orphans'
  | 'outline'
  | 'outline-color'
  | 'outline-offset'
  | 'outline-style'
  | 'outline-width'
  | 'overflow'
  | 'overflow-anchor'
  | 'overflow-block'
  | 'overflow-clip-box'
  | 'overflow-clip-margin'
  | 'overflow-inline'
  | 'overflow-wrap'
  | 'overflow-x'
  | 'overflow-y'
  | 'overlay'
  | 'overscroll-behavior'
  | 'overscroll-behavior-block'
  | 'overscroll-behavior-inline'
  | 'overscroll-behavior-x'
  | 'overscroll-behavior-y'
  | 'padding'
  | 'padding-block'
  | 'padding-block-end'
  | 'padding-block-start'
  | 'padding-bottom'
  | 'padding-inline'
  | 'padding-inline-end'
  | 'padding-inline-start'
  | 'padding-left'
  | 'padding-right'
  | 'padding-top'
  | 'page'
  | 'page-break-after'
  | 'page-break-before'
  | 'page-break-inside'
  | 'paint-order'
  | 'perspective'
  | 'perspective-origin'
  | 'place-content'
  | 'place-items'
  | 'place-self'
  | 'pointer-events'
  | 'position'
  | 'position-anchor'
  | 'position-area'
  | 'position-try'
  | 'position-try-fallbacks'
  | 'position-try-order'
  | 'position-visibility'
  | 'print-color-adjust'
  | 'quotes'
  | 'r'
  | 'resize'
  | 'right'
  | 'rotate'
  | 'row-gap'
  | 'ruby-align'
  | 'ruby-merge'
  | 'ruby-position'
  | 'rx'
  | 'ry'
  | 'scale'
  | 'scrollbar-color'
  | 'scrollbar-gutter'
  | 'scrollbar-width'
  | 'scroll-behavior'
  | 'scroll-margin'
  | 'scroll-margin-block'
  | 'scroll-margin-block-start'
  | 'scroll-margin-block-end'
  | 'scroll-margin-bottom'
  | 'scroll-margin-inline'
  | 'scroll-margin-inline-start'
  | 'scroll-margin-inline-end'
  | 'scroll-margin-left'
  | 'scroll-margin-right'
  | 'scroll-margin-top'
  | 'scroll-padding'
  | 'scroll-padding-block'
  | 'scroll-padding-block-start'
  | 'scroll-padding-block-end'
  | 'scroll-padding-bottom'
  | 'scroll-padding-inline'
  | 'scroll-padding-inline-start'
  | 'scroll-padding-inline-end'
  | 'scroll-padding-left'
  | 'scroll-padding-right'
  | 'scroll-padding-top'
  | 'scroll-snap-align'
  | 'scroll-snap-coordinate'
  | 'scroll-snap-destination'
  | 'scroll-snap-points-x'
  | 'scroll-snap-points-y'
  | 'scroll-snap-stop'
  | 'scroll-snap-type'
  | 'scroll-snap-type-x'
  | 'scroll-snap-type-y'
  | 'scroll-timeline'
  | 'scroll-timeline-axis'
  | 'scroll-timeline-name'
  | 'shape-image-threshold'
  | 'shape-margin'
  | 'shape-outside'
  | 'shape-rendering'
  | 'stroke'
  | 'stroke-dasharray'
  | 'stroke-dashoffset'
  | 'stroke-linecap'
  | 'stroke-linejoin'
  | 'stroke-miterlimit'
  | 'stroke-opacity'
  | 'stroke-width'
  | 'tab-size'
  | 'table-layout'
  | 'text-align'
  | 'text-align-last'
  | 'text-anchor'
  | 'text-combine-upright'
  | 'text-decoration'
  | 'text-decoration-color'
  | 'text-decoration-line'
  | 'text-decoration-skip'
  | 'text-decoration-skip-ink'
  | 'text-decoration-style'
  | 'text-decoration-thickness'
  | 'text-emphasis'
  | 'text-emphasis-color'
  | 'text-emphasis-position'
  | 'text-emphasis-style'
  | 'text-indent'
  | 'text-justify'
  | 'text-orientation'
  | 'text-overflow'
  | 'text-rendering'
  | 'text-shadow'
  | 'text-size-adjust'
  | 'text-spacing-trim'
  | 'text-transform'
  | 'text-underline-offset'
  | 'text-underline-position'
  | 'text-wrap'
  | 'text-wrap-mode'
  | 'text-wrap-style'
  | 'timeline-scope'
  | 'top'
  | 'touch-action'
  | 'transform'
  | 'transform-box'
  | 'transform-origin'
  | 'transform-style'
  | 'transition'
  | 'transition-behavior'
  | 'transition-delay'
  | 'transition-duration'
  | 'transition-property'
  | 'transition-timing-function'
  | 'translate'
  | 'unicode-bidi'
  | 'user-select'
  | 'vector-effect'
  | 'vertical-align'
  | 'view-timeline'
  | 'view-timeline-axis'
  | 'view-timeline-inset'
  | 'view-timeline-name'
  | 'view-transition-name'
  | 'visibility'
  | 'white-space'
  | 'white-space-collapse'
  | 'widows'
  | 'width'
  | 'will-change'
  | 'word-break'
  | 'word-spacing'
  | 'word-wrap'
  | 'writing-mode'
  | 'x'
  | 'y'
  | 'z-index'
  | 'zoom';

/**
 * Union of all StyleStore paths for the css properties supported in Webflow.
 */
export type WebflowCssStyleStorePath =
  | 'WebkitTextFillColor'
  | 'textStrokeColor'
  | 'textStrokeWidth'
  | 'accentColor'
  | 'alignContent'
  | 'alignItems'
  | 'alignSelf'
  | 'alignTracks'
  | 'all'
  | 'anchorName'
  | 'anchorScope'
  | 'animation'
  | 'animationComposition'
  | 'animationDelay'
  | 'animationDirection'
  | 'animationDuration'
  | 'animationFillMode'
  | 'animationIterationCount'
  | 'animationName'
  | 'animationPlayState'
  | 'animationRange'
  | 'animationRangeEnd'
  | 'animationRangeStart'
  | 'animationTimingFunction'
  | 'animationTimeline'
  | 'appearance'
  | 'aspectRatio'
  | 'azimuth'
  | 'backdropFilters'
  | 'backfaceVisibility'
  | 'backgroundImages'
  | 'backgroundImages'
  | 'backgroundBlendMode'
  | 'backgroundClip'
  | 'backgroundColor'
  | 'backgroundImages'
  | 'backgroundOrigin'
  | 'backgroundImages'
  | 'backgroundPositionX'
  | 'backgroundPositionY'
  | 'backgroundImages'
  | 'backgroundImages'
  | 'blockSize'
  | 'border'
  | 'borderBlock'
  | 'borderBlockColor'
  | 'borderBlockStyle'
  | 'borderBlockWidth'
  | 'borderBlockEnd'
  | 'borderBlockEndColor'
  | 'borderBlockEndStyle'
  | 'borderBlockEndWidth'
  | 'borderBlockStart'
  | 'borderBlockStartColor'
  | 'borderBlockStartStyle'
  | 'borderBlockStartWidth'
  | 'borderBottom'
  | 'borderBottomColor'
  | 'borderRadiusBottomLeft'
  | 'borderRadiusBottomRight'
  | 'borderBottomStyle'
  | 'borderBottomWidth'
  | 'borderCollapse'
  | 'borderColor'
  | 'borderEndEndRadius'
  | 'borderEndStartRadius'
  | 'borderImage'
  | 'borderImageOutset'
  | 'borderImageRepeat'
  | 'borderImageSlice'
  | 'borderImageSource'
  | 'borderImageWidth'
  | 'borderInline'
  | 'borderInlineEnd'
  | 'borderInlineColor'
  | 'borderInlineStyle'
  | 'borderInlineWidth'
  | 'borderInlineEndColor'
  | 'borderInlineEndStyle'
  | 'borderInlineEndWidth'
  | 'borderInlineStart'
  | 'borderInlineStartColor'
  | 'borderInlineStartStyle'
  | 'borderInlineStartWidth'
  | 'borderLeft'
  | 'borderLeftColor'
  | 'borderLeftStyle'
  | 'borderLeftWidth'
  | 'borderRadius'
  | 'borderRight'
  | 'borderRightColor'
  | 'borderRightStyle'
  | 'borderRightWidth'
  | 'borderSpacing'
  | 'borderStartEndRadius'
  | 'borderStartStartRadius'
  | 'borderStyle'
  | 'borderTop'
  | 'borderTopColor'
  | 'borderRadiusTopLeft'
  | 'borderRadiusTopRight'
  | 'borderTopStyle'
  | 'borderTopWidth'
  | 'borderWidth'
  | 'bottom'
  | 'boxAlign'
  | 'boxDecorationBreak'
  | 'boxDirection'
  | 'boxFlex'
  | 'boxFlexGroup'
  | 'boxLines'
  | 'boxOrdinalGroup'
  | 'boxOrient'
  | 'boxPack'
  | 'boxShadows'
  | 'boxSizing'
  | 'breakAfter'
  | 'breakBefore'
  | 'breakInside'
  | 'captionSide'
  | 'caret'
  | 'caretColor'
  | 'caretShape'
  | 'clear'
  | 'clip'
  | 'clipPath'
  | 'clipRule'
  | 'fontColor'
  | 'colorInterpolationFilters'
  | 'colorScheme'
  | 'columnCount'
  | 'columnFill'
  | 'columnGap'
  | 'columnRule'
  | 'columnRuleColor'
  | 'columnRuleStyle'
  | 'columnRuleWidth'
  | 'columnSpan'
  | 'columnWidth'
  | 'columns'
  | 'contain'
  | 'containIntrinsicSize'
  | 'containIntrinsicBlockSize'
  | 'containIntrinsicHeight'
  | 'containIntrinsicInlineSize'
  | 'containIntrinsicWidth'
  | 'container'
  | 'containerName'
  | 'containerType'
  | 'content'
  | 'contentVisibility'
  | 'counterIncrement'
  | 'counterReset'
  | 'counterSet'
  | 'cursor'
  | 'd'
  | 'cx'
  | 'cy'
  | 'direction'
  | 'display'
  | 'dominantBaseline'
  | 'emptyCells'
  | 'fieldSizing'
  | 'fill'
  | 'fillOpacity'
  | 'fillRule'
  | 'filters'
  | 'flex'
  | 'flexBasis'
  | 'flexDirection'
  | 'flexFlow'
  | 'flexGrow'
  | 'flexShrink'
  | 'flexWrap'
  | 'float'
  | 'font'
  | 'fontFamily'
  | 'fontFeatureSettings'
  | 'fontKerning'
  | 'fontLanguageOverride'
  | 'fontOpticalSizing'
  | 'fontPalette'
  | 'fontVariationSettings'
  | 'fontSize'
  | 'fontSizeAdjust'
  | 'fontSmooth'
  | 'fontStretch'
  | 'fontStyle'
  | 'fontSynthesis'
  | 'fontSynthesisPosition'
  | 'fontSynthesisSmallCaps'
  | 'fontSynthesisStyle'
  | 'fontSynthesisWeight'
  | 'fontVariant'
  | 'fontVariantAlternates'
  | 'fontVariantCaps'
  | 'fontVariantEastAsian'
  | 'fontVariantLigatures'
  | 'fontVariantNumeric'
  | 'fontVariantPosition'
  | 'fontWeight'
  | 'forcedColorAdjust'
  | 'gap'
  | 'grid'
  | 'gridArea'
  | 'gridAutoColumns'
  | 'gridAutoFlow'
  | 'gridAutoRows'
  | 'gridColumn'
  | 'gridColumnEnd'
  | 'gridColumnGap'
  | 'gridColumnStart'
  | 'gridGap'
  | 'gridRow'
  | 'gridRowEnd'
  | 'gridRowGap'
  | 'gridRowStart'
  | 'gridTemplate'
  | 'gridTemplateAreas'
  | 'gridTemplateColumns'
  | 'gridTemplateRows'
  | 'hangingPunctuation'
  | 'height'
  | 'hyphenateCharacter'
  | 'hyphenateLimitChars'
  | 'hyphens'
  | 'imageOrientation'
  | 'imageRendering'
  | 'imageResolution'
  | 'imeMode'
  | 'initialLetter'
  | 'initialLetterAlign'
  | 'inlineSize'
  | 'inputSecurity'
  | 'inset'
  | 'insetBlock'
  | 'insetBlockEnd'
  | 'insetBlockStart'
  | 'insetInline'
  | 'insetInlineEnd'
  | 'insetInlineStart'
  | 'interpolateSize'
  | 'isolation'
  | 'justifyContent'
  | 'justifyItems'
  | 'justifySelf'
  | 'justifyTracks'
  | 'left'
  | 'letterSpacing'
  | 'lineBreak'
  | 'lineHeight'
  | 'lineHeightStep'
  | 'listStyle'
  | 'listStyleImage'
  | 'listStylePosition'
  | 'listStyleType'
  | 'margin'
  | 'marginBlock'
  | 'marginBlockEnd'
  | 'marginBlockStart'
  | 'marginBottom'
  | 'marginInline'
  | 'marginInlineEnd'
  | 'marginInlineStart'
  | 'marginLeft'
  | 'marginRight'
  | 'marginTop'
  | 'marginTrim'
  | 'marker'
  | 'markerEnd'
  | 'markerMid'
  | 'markerStart'
  | 'mask'
  | 'maskBorder'
  | 'maskBorderMode'
  | 'maskBorderOutset'
  | 'maskBorderRepeat'
  | 'maskBorderSlice'
  | 'maskBorderSource'
  | 'maskBorderWidth'
  | 'maskClip'
  | 'maskComposite'
  | 'maskImage'
  | 'maskMode'
  | 'maskOrigin'
  | 'maskPosition'
  | 'maskRepeat'
  | 'maskSize'
  | 'maskType'
  | 'masonryAutoFlow'
  | 'mathDepth'
  | 'mathShift'
  | 'mathStyle'
  | 'maxBlockSize'
  | 'maxHeight'
  | 'maxInlineSize'
  | 'maxLines'
  | 'maxWidth'
  | 'minBlockSize'
  | 'minHeight'
  | 'minInlineSize'
  | 'minWidth'
  | 'mixBlendMode'
  | 'objectFit'
  | 'objectPosition'
  | 'offset'
  | 'offsetAnchor'
  | 'offsetDistance'
  | 'offsetPath'
  | 'offsetPosition'
  | 'offsetRotate'
  | 'opacity'
  | 'order'
  | 'orphans'
  | 'outline'
  | 'outlineColor'
  | 'outlineOffset'
  | 'outlineStyle'
  | 'outlineWidth'
  | 'overflow'
  | 'overflowAnchor'
  | 'overflowBlock'
  | 'overflowClipBox'
  | 'overflowClipMargin'
  | 'overflowInline'
  | 'overflowWrap'
  | 'overflowX'
  | 'overflowY'
  | 'overlay'
  | 'overscrollBehavior'
  | 'overscrollBehaviorBlock'
  | 'overscrollBehaviorInline'
  | 'overscrollBehaviorX'
  | 'overscrollBehaviorY'
  | 'padding'
  | 'paddingBlock'
  | 'paddingBlockEnd'
  | 'paddingBlockStart'
  | 'paddingBottom'
  | 'paddingInline'
  | 'paddingInlineEnd'
  | 'paddingInlineStart'
  | 'paddingLeft'
  | 'paddingRight'
  | 'paddingTop'
  | 'page'
  | 'pageBreakAfter'
  | 'pageBreakBefore'
  | 'pageBreakInside'
  | 'paintOrder'
  | 'perspective'
  | 'perspectiveOrigin'
  | 'placeContent'
  | 'placeItems'
  | 'placeSelf'
  | 'pointerEvents'
  | 'position'
  | 'positionAnchor'
  | 'positionArea'
  | 'positionTry'
  | 'positionTryFallbacks'
  | 'positionTryOrder'
  | 'positionVisibility'
  | 'printColorAdjust'
  | 'quotes'
  | 'r'
  | 'resize'
  | 'right'
  | 'rotate'
  | 'rowGap'
  | 'rubyAlign'
  | 'rubyMerge'
  | 'rubyPosition'
  | 'rx'
  | 'ry'
  | 'scale'
  | 'scrollbarColor'
  | 'scrollbarGutter'
  | 'scrollbarWidth'
  | 'scrollBehavior'
  | 'scrollMargin'
  | 'scrollMarginBlock'
  | 'scrollMarginBlockStart'
  | 'scrollMarginBlockEnd'
  | 'scrollMarginBottom'
  | 'scrollMarginInline'
  | 'scrollMarginInlineStart'
  | 'scrollMarginInlineEnd'
  | 'scrollMarginLeft'
  | 'scrollMarginRight'
  | 'scrollMarginTop'
  | 'scrollPadding'
  | 'scrollPaddingBlock'
  | 'scrollPaddingBlockStart'
  | 'scrollPaddingBlockEnd'
  | 'scrollPaddingBottom'
  | 'scrollPaddingInline'
  | 'scrollPaddingInlineStart'
  | 'scrollPaddingInlineEnd'
  | 'scrollPaddingLeft'
  | 'scrollPaddingRight'
  | 'scrollPaddingTop'
  | 'scrollSnapAlign'
  | 'scrollSnapCoordinate'
  | 'scrollSnapDestination'
  | 'scrollSnapPointsX'
  | 'scrollSnapPointsY'
  | 'scrollSnapStop'
  | 'scrollSnapType'
  | 'scrollSnapTypeX'
  | 'scrollSnapTypeY'
  | 'scrollTimeline'
  | 'scrollTimelineAxis'
  | 'scrollTimelineName'
  | 'shapeImageThreshold'
  | 'shapeMargin'
  | 'shapeOutside'
  | 'shapeRendering'
  | 'stroke'
  | 'strokeDasharray'
  | 'strokeDashoffset'
  | 'strokeLinecap'
  | 'strokeLinejoin'
  | 'strokeMiterlimit'
  | 'strokeOpacity'
  | 'strokeWidth'
  | 'tabSize'
  | 'tableLayout'
  | 'textAlign'
  | 'textAlignLast'
  | 'textAnchor'
  | 'textCombineUpright'
  | 'textDecoration'
  | 'textDecorationColor'
  | 'textDecorationLine'
  | 'textDecorationSkip'
  | 'textDecorationSkipInk'
  | 'textDecorationStyle'
  | 'textDecorationThickness'
  | 'textEmphasis'
  | 'textEmphasisColor'
  | 'textEmphasisPosition'
  | 'textEmphasisStyle'
  | 'textIndent'
  | 'textJustify'
  | 'textOrientation'
  | 'textOverflow'
  | 'textRendering'
  | 'textShadows'
  | 'textSizeAdjust'
  | 'textSpacingTrim'
  | 'textTransform'
  | 'textUnderlineOffset'
  | 'textUnderlinePosition'
  | 'textWrap'
  | 'textWrapMode'
  | 'textWrapStyle'
  | 'timelineScope'
  | 'top'
  | 'touchAction'
  | 'transforms'
  | 'transformBox'
  | 'transformOrigin'
  | 'transformStyle'
  | 'transitions'
  | 'transitionBehavior'
  | 'transitions'
  | 'transitions'
  | 'transitions'
  | 'transitions'
  | 'translate'
  | 'unicodeBidi'
  | 'userSelect'
  | 'vectorEffect'
  | 'verticalAlign'
  | 'viewTimeline'
  | 'viewTimelineAxis'
  | 'viewTimelineInset'
  | 'viewTimelineName'
  | 'viewTransitionName'
  | 'visibility'
  | 'whiteSpace'
  | 'whiteSpaceCollapse'
  | 'widows'
  | 'width'
  | 'willChange'
  | 'wordBreak'
  | 'wordSpacing'
  | 'wordWrap'
  | 'writingMode'
  | 'x'
  | 'y'
  | 'zIndex'
  | 'zoom';

/**
 * The style panel props for the Custom properties section.
 */
export type StylePanel$ExpandedCssValues = {
  WebkitTextFillColor: StylePanelProperty<string>;
  accentColor: StylePanelProperty<string>;
  alignTracks: StylePanelProperty<string>;
  all: StylePanelProperty<string>;
  anchorName: StylePanelProperty<string>;
  anchorScope: StylePanelProperty<string>;
  animation: StylePanelProperty<string>;
  animationComposition: StylePanelProperty<string>;
  animationDelay: StylePanelProperty<string>;
  animationDirection: StylePanelProperty<string>;
  animationDuration: StylePanelProperty<string>;
  animationFillMode: StylePanelProperty<string>;
  animationIterationCount: StylePanelProperty<string>;
  animationName: StylePanelProperty<string>;
  animationPlayState: StylePanelProperty<string>;
  animationRange: StylePanelProperty<string>;
  animationRangeEnd: StylePanelProperty<string>;
  animationRangeStart: StylePanelProperty<string>;
  animationTimingFunction: StylePanelProperty<string>;
  animationTimeline: StylePanelProperty<string>;
  appearance: StylePanelProperty<string>;
  azimuth: StylePanelProperty<string>;
  backgroundBlendMode: StylePanelProperty<string>;
  backgroundOrigin: StylePanelProperty<string>;
  backgroundPositionX: StylePanelProperty<string>;
  backgroundPositionY: StylePanelProperty<string>;
  blockSize: StylePanelProperty<string>;
  border: StylePanelProperty<string>;
  borderBlock: StylePanelProperty<string>;
  borderBlockColor: StylePanelProperty<string>;
  borderBlockStyle: StylePanelProperty<string>;
  borderBlockWidth: StylePanelProperty<string>;
  borderBlockEnd: StylePanelProperty<string>;
  borderBlockEndColor: StylePanelProperty<string>;
  borderBlockEndStyle: StylePanelProperty<string>;
  borderBlockEndWidth: StylePanelProperty<string>;
  borderBlockStart: StylePanelProperty<string>;
  borderBlockStartColor: StylePanelProperty<string>;
  borderBlockStartStyle: StylePanelProperty<string>;
  borderBlockStartWidth: StylePanelProperty<string>;
  borderBottom: StylePanelProperty<string>;
  borderCollapse: StylePanelProperty<string>;
  borderEndEndRadius: StylePanelProperty<string>;
  borderEndStartRadius: StylePanelProperty<string>;
  borderImage: StylePanelProperty<string>;
  borderImageOutset: StylePanelProperty<string>;
  borderImageRepeat: StylePanelProperty<string>;
  borderImageSlice: StylePanelProperty<string>;
  borderImageSource: StylePanelProperty<string>;
  borderImageWidth: StylePanelProperty<string>;
  borderInline: StylePanelProperty<string>;
  borderInlineEnd: StylePanelProperty<string>;
  borderInlineColor: StylePanelProperty<string>;
  borderInlineStyle: StylePanelProperty<string>;
  borderInlineWidth: StylePanelProperty<string>;
  borderInlineEndColor: StylePanelProperty<string>;
  borderInlineEndStyle: StylePanelProperty<string>;
  borderInlineEndWidth: StylePanelProperty<string>;
  borderInlineStart: StylePanelProperty<string>;
  borderInlineStartColor: StylePanelProperty<string>;
  borderInlineStartStyle: StylePanelProperty<string>;
  borderInlineStartWidth: StylePanelProperty<string>;
  borderLeft: StylePanelProperty<string>;
  borderRight: StylePanelProperty<string>;
  borderSpacing: StylePanelProperty<string>;
  borderStartEndRadius: StylePanelProperty<string>;
  borderStartStartRadius: StylePanelProperty<string>;
  borderTop: StylePanelProperty<string>;
  boxAlign: StylePanelProperty<string>;
  boxDecorationBreak: StylePanelProperty<string>;
  boxDirection: StylePanelProperty<string>;
  boxFlex: StylePanelProperty<string>;
  boxFlexGroup: StylePanelProperty<string>;
  boxLines: StylePanelProperty<string>;
  boxOrdinalGroup: StylePanelProperty<string>;
  boxOrient: StylePanelProperty<string>;
  boxPack: StylePanelProperty<string>;
  breakAfter: StylePanelProperty<string>;
  breakBefore: StylePanelProperty<string>;
  breakInside: StylePanelProperty<string>;
  captionSide: StylePanelProperty<string>;
  caret: StylePanelProperty<string>;
  caretColor: StylePanelProperty<string>;
  caretShape: StylePanelProperty<string>;
  clip: StylePanelProperty<string>;
  clipPath: StylePanelProperty<string>;
  clipRule: StylePanelProperty<string>;
  colorInterpolationFilters: StylePanelProperty<string>;
  colorScheme: StylePanelProperty<string>;
  columnFill: StylePanelProperty<string>;
  columnRule: StylePanelProperty<string>;
  columnWidth: StylePanelProperty<string>;
  columns: StylePanelProperty<string>;
  contain: StylePanelProperty<string>;
  containIntrinsicSize: StylePanelProperty<string>;
  containIntrinsicBlockSize: StylePanelProperty<string>;
  containIntrinsicHeight: StylePanelProperty<string>;
  containIntrinsicInlineSize: StylePanelProperty<string>;
  containIntrinsicWidth: StylePanelProperty<string>;
  container: StylePanelProperty<string>;
  containerName: StylePanelProperty<string>;
  containerType: StylePanelProperty<string>;
  content: StylePanelProperty<string>;
  contentVisibility: StylePanelProperty<string>;
  counterIncrement: StylePanelProperty<string>;
  counterReset: StylePanelProperty<string>;
  counterSet: StylePanelProperty<string>;
  d: StylePanelProperty<string>;
  cx: StylePanelProperty<string>;
  cy: StylePanelProperty<string>;
  dominantBaseline: StylePanelProperty<string>;
  emptyCells: StylePanelProperty<string>;
  fieldSizing: StylePanelProperty<string>;
  fill: StylePanelProperty<string>;
  fillOpacity: StylePanelProperty<string>;
  fillRule: StylePanelProperty<string>;
  flex: StylePanelProperty<string>;
  flexFlow: StylePanelProperty<string>;
  font: StylePanelProperty<string>;
  fontFeatureSettings: StylePanelProperty<string>;
  fontKerning: StylePanelProperty<string>;
  fontLanguageOverride: StylePanelProperty<string>;
  fontOpticalSizing: StylePanelProperty<string>;
  fontPalette: StylePanelProperty<string>;
  fontSizeAdjust: StylePanelProperty<string>;
  fontSmooth: StylePanelProperty<string>;
  fontStretch: StylePanelProperty<string>;
  fontSynthesis: StylePanelProperty<string>;
  fontSynthesisPosition: StylePanelProperty<string>;
  fontSynthesisSmallCaps: StylePanelProperty<string>;
  fontSynthesisStyle: StylePanelProperty<string>;
  fontSynthesisWeight: StylePanelProperty<string>;
  fontVariant: StylePanelProperty<string>;
  fontVariantAlternates: StylePanelProperty<string>;
  fontVariantCaps: StylePanelProperty<string>;
  fontVariantEastAsian: StylePanelProperty<string>;
  fontVariantLigatures: StylePanelProperty<string>;
  fontVariantNumeric: StylePanelProperty<string>;
  fontVariantPosition: StylePanelProperty<string>;
  forcedColorAdjust: StylePanelProperty<string>;
  gap: StylePanelProperty<string>;
  grid: StylePanelProperty<string>;
  gridColumn: StylePanelProperty<string>;
  gridGap: StylePanelProperty<string>;
  gridRow: StylePanelProperty<string>;
  gridTemplate: StylePanelProperty<string>;
  hangingPunctuation: StylePanelProperty<string>;
  hyphenateCharacter: StylePanelProperty<string>;
  hyphenateLimitChars: StylePanelProperty<string>;
  hyphens: StylePanelProperty<string>;
  imageOrientation: StylePanelProperty<string>;
  imageRendering: StylePanelProperty<string>;
  imageResolution: StylePanelProperty<string>;
  imeMode: StylePanelProperty<string>;
  initialLetter: StylePanelProperty<string>;
  initialLetterAlign: StylePanelProperty<string>;
  inlineSize: StylePanelProperty<string>;
  inputSecurity: StylePanelProperty<string>;
  inset: StylePanelProperty<string>;
  insetBlock: StylePanelProperty<string>;
  insetBlockEnd: StylePanelProperty<string>;
  insetBlockStart: StylePanelProperty<string>;
  insetInline: StylePanelProperty<string>;
  insetInlineEnd: StylePanelProperty<string>;
  insetInlineStart: StylePanelProperty<string>;
  interpolateSize: StylePanelProperty<string>;
  isolation: StylePanelProperty<string>;
  justifyTracks: StylePanelProperty<string>;
  lineBreak: StylePanelProperty<string>;
  lineHeightStep: StylePanelProperty<string>;
  listStyle: StylePanelProperty<string>;
  listStyleImage: StylePanelProperty<string>;
  listStylePosition: StylePanelProperty<string>;
  margin: StylePanelProperty<string>;
  marginBlock: StylePanelProperty<string>;
  marginBlockEnd: StylePanelProperty<string>;
  marginBlockStart: StylePanelProperty<string>;
  marginInline: StylePanelProperty<string>;
  marginInlineEnd: StylePanelProperty<string>;
  marginInlineStart: StylePanelProperty<string>;
  marginTrim: StylePanelProperty<string>;
  marker: StylePanelProperty<string>;
  markerEnd: StylePanelProperty<string>;
  markerMid: StylePanelProperty<string>;
  markerStart: StylePanelProperty<string>;
  mask: StylePanelProperty<string>;
  maskBorder: StylePanelProperty<string>;
  maskBorderMode: StylePanelProperty<string>;
  maskBorderOutset: StylePanelProperty<string>;
  maskBorderRepeat: StylePanelProperty<string>;
  maskBorderSlice: StylePanelProperty<string>;
  maskBorderSource: StylePanelProperty<string>;
  maskBorderWidth: StylePanelProperty<string>;
  maskClip: StylePanelProperty<string>;
  maskComposite: StylePanelProperty<string>;
  maskImage: StylePanelProperty<string>;
  maskMode: StylePanelProperty<string>;
  maskOrigin: StylePanelProperty<string>;
  maskPosition: StylePanelProperty<string>;
  maskRepeat: StylePanelProperty<string>;
  maskSize: StylePanelProperty<string>;
  maskType: StylePanelProperty<string>;
  masonryAutoFlow: StylePanelProperty<string>;
  mathDepth: StylePanelProperty<string>;
  mathShift: StylePanelProperty<string>;
  mathStyle: StylePanelProperty<string>;
  maxBlockSize: StylePanelProperty<string>;
  maxInlineSize: StylePanelProperty<string>;
  maxLines: StylePanelProperty<string>;
  minBlockSize: StylePanelProperty<string>;
  minInlineSize: StylePanelProperty<string>;
  offset: StylePanelProperty<string>;
  offsetAnchor: StylePanelProperty<string>;
  offsetDistance: StylePanelProperty<string>;
  offsetPath: StylePanelProperty<string>;
  offsetPosition: StylePanelProperty<string>;
  offsetRotate: StylePanelProperty<string>;
  orphans: StylePanelProperty<string>;
  outline: StylePanelProperty<string>;
  overflowAnchor: StylePanelProperty<string>;
  overflowBlock: StylePanelProperty<string>;
  overflowClipBox: StylePanelProperty<string>;
  overflowClipMargin: StylePanelProperty<string>;
  overflowInline: StylePanelProperty<string>;
  overlay: StylePanelProperty<string>;
  overscrollBehavior: StylePanelProperty<string>;
  overscrollBehaviorBlock: StylePanelProperty<string>;
  overscrollBehaviorInline: StylePanelProperty<string>;
  overscrollBehaviorX: StylePanelProperty<string>;
  overscrollBehaviorY: StylePanelProperty<string>;
  padding: StylePanelProperty<string>;
  paddingBlock: StylePanelProperty<string>;
  paddingBlockEnd: StylePanelProperty<string>;
  paddingBlockStart: StylePanelProperty<string>;
  paddingInline: StylePanelProperty<string>;
  paddingInlineEnd: StylePanelProperty<string>;
  paddingInlineStart: StylePanelProperty<string>;
  page: StylePanelProperty<string>;
  pageBreakAfter: StylePanelProperty<string>;
  pageBreakBefore: StylePanelProperty<string>;
  pageBreakInside: StylePanelProperty<string>;
  paintOrder: StylePanelProperty<string>;
  placeContent: StylePanelProperty<string>;
  placeItems: StylePanelProperty<string>;
  placeSelf: StylePanelProperty<string>;
  positionAnchor: StylePanelProperty<string>;
  positionArea: StylePanelProperty<string>;
  positionTry: StylePanelProperty<string>;
  positionTryFallbacks: StylePanelProperty<string>;
  positionTryOrder: StylePanelProperty<string>;
  positionVisibility: StylePanelProperty<string>;
  printColorAdjust: StylePanelProperty<string>;
  quotes: StylePanelProperty<string>;
  r: StylePanelProperty<string>;
  resize: StylePanelProperty<string>;
  rotate: StylePanelProperty<string>;
  rowGap: StylePanelProperty<string>;
  rubyAlign: StylePanelProperty<string>;
  rubyMerge: StylePanelProperty<string>;
  rubyPosition: StylePanelProperty<string>;
  rx: StylePanelProperty<string>;
  ry: StylePanelProperty<string>;
  scale: StylePanelProperty<string>;
  scrollbarColor: StylePanelProperty<string>;
  scrollbarGutter: StylePanelProperty<string>;
  scrollbarWidth: StylePanelProperty<string>;
  scrollBehavior: StylePanelProperty<string>;
  scrollMargin: StylePanelProperty<string>;
  scrollMarginBlock: StylePanelProperty<string>;
  scrollMarginBlockStart: StylePanelProperty<string>;
  scrollMarginBlockEnd: StylePanelProperty<string>;
  scrollMarginBottom: StylePanelProperty<string>;
  scrollMarginInline: StylePanelProperty<string>;
  scrollMarginInlineStart: StylePanelProperty<string>;
  scrollMarginInlineEnd: StylePanelProperty<string>;
  scrollMarginLeft: StylePanelProperty<string>;
  scrollMarginRight: StylePanelProperty<string>;
  scrollMarginTop: StylePanelProperty<string>;
  scrollPadding: StylePanelProperty<string>;
  scrollPaddingBlock: StylePanelProperty<string>;
  scrollPaddingBlockStart: StylePanelProperty<string>;
  scrollPaddingBlockEnd: StylePanelProperty<string>;
  scrollPaddingBottom: StylePanelProperty<string>;
  scrollPaddingInline: StylePanelProperty<string>;
  scrollPaddingInlineStart: StylePanelProperty<string>;
  scrollPaddingInlineEnd: StylePanelProperty<string>;
  scrollPaddingLeft: StylePanelProperty<string>;
  scrollPaddingRight: StylePanelProperty<string>;
  scrollPaddingTop: StylePanelProperty<string>;
  scrollSnapAlign: StylePanelProperty<string>;
  scrollSnapCoordinate: StylePanelProperty<string>;
  scrollSnapDestination: StylePanelProperty<string>;
  scrollSnapPointsX: StylePanelProperty<string>;
  scrollSnapPointsY: StylePanelProperty<string>;
  scrollSnapStop: StylePanelProperty<string>;
  scrollSnapType: StylePanelProperty<string>;
  scrollSnapTypeX: StylePanelProperty<string>;
  scrollSnapTypeY: StylePanelProperty<string>;
  scrollTimeline: StylePanelProperty<string>;
  scrollTimelineAxis: StylePanelProperty<string>;
  scrollTimelineName: StylePanelProperty<string>;
  shapeImageThreshold: StylePanelProperty<string>;
  shapeMargin: StylePanelProperty<string>;
  shapeOutside: StylePanelProperty<string>;
  shapeRendering: StylePanelProperty<string>;
  stroke: StylePanelProperty<string>;
  strokeDasharray: StylePanelProperty<string>;
  strokeDashoffset: StylePanelProperty<string>;
  strokeLinecap: StylePanelProperty<string>;
  strokeLinejoin: StylePanelProperty<string>;
  strokeMiterlimit: StylePanelProperty<string>;
  strokeOpacity: StylePanelProperty<string>;
  strokeWidth: StylePanelProperty<string>;
  tabSize: StylePanelProperty<string>;
  tableLayout: StylePanelProperty<string>;
  textAlignLast: StylePanelProperty<string>;
  textAnchor: StylePanelProperty<string>;
  textCombineUpright: StylePanelProperty<string>;
  textDecorationLine: StylePanelProperty<string>;
  textDecorationSkip: StylePanelProperty<string>;
  textEmphasis: StylePanelProperty<string>;
  textEmphasisColor: StylePanelProperty<string>;
  textEmphasisPosition: StylePanelProperty<string>;
  textEmphasisStyle: StylePanelProperty<string>;
  textJustify: StylePanelProperty<string>;
  textOrientation: StylePanelProperty<string>;
  textRendering: StylePanelProperty<string>;
  textSizeAdjust: StylePanelProperty<string>;
  textSpacingTrim: StylePanelProperty<string>;
  textWrap: StylePanelProperty<string>;
  textWrapMode: StylePanelProperty<string>;
  textWrapStyle: StylePanelProperty<string>;
  timelineScope: StylePanelProperty<string>;
  touchAction: StylePanelProperty<string>;
  transformBox: StylePanelProperty<string>;
  transitionBehavior: StylePanelProperty<string>;
  translate: StylePanelProperty<string>;
  unicodeBidi: StylePanelProperty<string>;
  userSelect: StylePanelProperty<string>;
  vectorEffect: StylePanelProperty<string>;
  viewTimeline: StylePanelProperty<string>;
  viewTimelineAxis: StylePanelProperty<string>;
  viewTimelineInset: StylePanelProperty<string>;
  viewTimelineName: StylePanelProperty<string>;
  viewTransitionName: StylePanelProperty<string>;
  whiteSpaceCollapse: StylePanelProperty<string>;
  widows: StylePanelProperty<string>;
  willChange: StylePanelProperty<string>;
  wordSpacing: StylePanelProperty<string>;
  wordWrap: StylePanelProperty<string>;
  writingMode: StylePanelProperty<string>;
  x: StylePanelProperty<string>;
  y: StylePanelProperty<string>;
  zoom: StylePanelProperty<string>;
};

/**
 * Used predominantly in the StyleStore, CustomProperties maps a custom css property
 * to a StyleProperty value. In this case, the value is always a "string" because it is
 * custom.
 *
 * Note: Not to be confused with `CssVariables`.
 */
export type CustomProperties = Record<
  WebflowCssStyleStorePath,
  StyleProperty<KeywordValue<string>>
>;

/**
 * Any CSS Variable properties in the Style Panel, including Webflow-specific
 * abstractions like Variable Modes which take the form of `---mode--${...}`.
 *
 * Note: Not to be confused with `CustomProperties`.
 */
export type CssVariables = Record<
  `--${string}`,
  StyleProperty<StyleStore$ValueType>
>;

/**
 * CSS_PROPERTY_DETAILS is the source of truth for every Webflow-supported style property,
 * containing metadata for each property like the path to the property in the StyleStore,
 * whether the property is inheritable, the section it belongs to, etc.
 */
export const CSS_PROPERTY_DETAILS: {
  [Property in WebflowCssPropertyFull]: CssPropertyDetail_Internal<
    WebflowCssStyleStorePath,
    WebflowCssPropertyFull
  >;
} = {
  '-webkit-text-fill-color': {
    section: 'expandedCss',
    default: 'currentcolor',
    path: 'WebkitTextFillColor' as const,
    typographic: true,
  },
  '-webkit-text-stroke-color': {
    section: 'typography',
    path: 'textStrokeColor' as const,
    typographic: true,
    color: true,
    allowSwatches: true,
    supportedVariableTypes: ['color'],
    placeholder: 'Transparent',
  },
  '-webkit-text-stroke-width': {
    section: 'typography',
    path: 'textStrokeWidth' as const,
    typographic: true,
    units: ['px', 'em', '%'],
    defaultUnit: 'px',
    min: 0,
    max: 500,
    dimensional: true,
    supportedVariableTypes: ['length'],
  },
  'accent-color': {
    section: 'expandedCss',
    default: 'auto',
    path: 'accentColor' as const,
    typographic: true,
  },
  'align-content': {
    section: 'layout',
    path: 'alignContent' as const,
    default: 'stretch',
    computedFrom: ['place-content'],
  },
  'align-items': {
    section: 'layout',
    path: 'alignItems' as const,
    default: 'stretch',
    computedFrom: ['place-items'],
  },
  'align-self': {
    section: 'layout',
    path: 'alignSelf' as const,
    default: 'auto',
    computedFrom: ['place-self'],
  },
  'align-tracks': {
    section: 'expandedCss',
    default: 'normal',
    path: 'alignTracks' as const,
  },
  all: {
    section: 'expandedCss',
    default: 'noPracticalInitialValue',
    path: 'all' as const,
  },
  'anchor-name': {
    section: 'expandedCss',
    default: 'none',
    path: 'anchorName' as const,
  },
  'anchor-scope': {
    section: 'expandedCss',
    default: 'none',
    path: 'anchorScope' as const,
  },
  animation: {
    section: 'expandedCss',
    computed: [
      'animation-name',
      'animation-duration',
      'animation-timing-function',
      'animation-delay',
      'animation-direction',
      'animation-iteration-count',
      'animation-fill-mode',
      'animation-play-state',
      'animation-timeline',
    ],
    default: 'none 0s ease 0s 1 normal none running auto',
    path: 'animation' as const,
  },
  'animation-composition': {
    section: 'expandedCss',
    default: 'replace',
    path: 'animationComposition' as const,
  },
  'animation-delay': {
    section: 'expandedCss',
    default: '0s',
    path: 'animationDelay' as const,
    computedFrom: ['animation'],
  },
  'animation-direction': {
    section: 'expandedCss',
    default: 'normal',
    path: 'animationDirection' as const,
    computedFrom: ['animation'],
  },
  'animation-duration': {
    section: 'expandedCss',
    default: '0s',
    path: 'animationDuration' as const,
    computedFrom: ['animation'],
  },
  'animation-fill-mode': {
    section: 'expandedCss',
    default: 'none',
    path: 'animationFillMode' as const,
    computedFrom: ['animation'],
  },
  'animation-iteration-count': {
    section: 'expandedCss',
    default: '1',
    path: 'animationIterationCount' as const,
    computedFrom: ['animation'],
  },
  'animation-name': {
    section: 'expandedCss',
    default: 'none',
    path: 'animationName' as const,
    computedFrom: ['animation'],
  },
  'animation-play-state': {
    section: 'expandedCss',
    default: 'running',
    path: 'animationPlayState' as const,
    computedFrom: ['animation'],
  },
  'animation-range': {
    section: 'expandedCss',
    computed: ['animation-range-start', 'animation-range-end'],
    default: 'normal normal',
    path: 'animationRange' as const,
  },
  'animation-range-end': {
    section: 'expandedCss',
    default: 'normal',
    path: 'animationRangeEnd' as const,
    computedFrom: ['animation-range'],
  },
  'animation-range-start': {
    section: 'expandedCss',
    default: 'normal',
    path: 'animationRangeStart' as const,
    computedFrom: ['animation-range'],
  },
  'animation-timing-function': {
    section: 'expandedCss',
    default: 'ease',
    path: 'animationTimingFunction' as const,
    computedFrom: ['animation'],
  },
  'animation-timeline': {
    section: 'expandedCss',
    default: 'auto',
    path: 'animationTimeline' as const,
    computedFrom: ['animation'],
  },
  appearance: {
    section: 'expandedCss',
    default: 'none',
    path: 'appearance' as const,
  },
  'aspect-ratio': {
    section: 'sizing',
    path: 'aspectRatio' as const,
    default: 'auto',
  },
  azimuth: {
    section: 'expandedCss',
    default: 'center',
    path: 'azimuth' as const,
    typographic: true,
  },
  'backdrop-filter': {
    section: 'effects',
    path: 'backdropFilters' as const,
    supportedVariableTypes: ['length', 'percentage'],
    multi: true,
  },
  'backface-visibility': {
    section: 'transitionsAndTransforms',
    path: 'backfaceVisibility' as const,
    default: 'visible',
  },
  background: {
    section: 'background',
    computed: [
      'background-image',
      'background-position',
      'background-size',
      'background-repeat',
      'background-origin',
      'background-clip',
      'background-attachment',
      'background-color',
    ],
    path: 'backgroundImages' as const,
    multi: true,
  },
  'background-attachment': {
    section: 'background',
    path: 'backgroundImages' as const,
    combo: 'attachment',
    default: 'scroll',
    computedFrom: ['background'],
  },
  'background-blend-mode': {
    section: 'expandedCss',
    default: 'normal',
    path: 'backgroundBlendMode' as const,
  },
  'background-clip': {
    section: 'background',
    path: 'backgroundClip' as const,
    default: 'border-box',
    computedFrom: ['background'],
  },
  'background-color': {
    section: 'background',
    path: 'backgroundColor' as const,
    color: true,
    allowSwatches: true,
    supportedVariableTypes: ['color'],
    default: 'transparent',
    computedFrom: ['background'],
  },
  'background-image': {
    section: 'background',
    path: 'backgroundImages' as const,
    combo: 'image',
    allowSwatches: true,
    supportedVariableTypes: ['color'],
    computedFrom: ['background'],
  },
  'background-origin': {
    section: 'expandedCss',
    default: 'padding-box',
    path: 'backgroundOrigin' as const,
    computedFrom: ['background'],
  },
  'background-position': {
    section: 'background',
    computed: ['background-position-x', 'background-position-y'],
    path: 'backgroundImages' as const,
    combo: 'position',
    computedFrom: ['background'],
  },
  'background-position-x': {
    section: 'expandedCss',
    default: '0%',
    path: 'backgroundPositionX' as const,
    computedFrom: ['background', 'background-position'],
  },
  'background-position-y': {
    section: 'expandedCss',
    default: '0%',
    path: 'backgroundPositionY' as const,
    computedFrom: ['background', 'background-position'],
  },
  'background-repeat': {
    section: 'background',
    path: 'backgroundImages' as const,
    combo: 'repeat',
    default: 'repeat',
    computedFrom: ['background'],
  },
  'background-size': {
    section: 'background',
    path: 'backgroundImages' as const,
    combo: 'size',
    computedFrom: ['background'],
  },
  'block-size': {
    section: 'expandedCss',
    default: 'auto',
    path: 'blockSize' as const,
  },
  border: {
    section: 'expandedCss',
    computed: ['border-width', 'border-style', 'border-color'],
    default:
      'medium medium medium medium none none none none currentcolor currentcolor currentcolor currentcolor',
    path: 'border' as const,
  },
  'border-block': {
    section: 'expandedCss',
    computed: [
      'border-block-width',
      'border-block-style',
      'border-block-color',
    ],
    default: 'medium none currentcolor',
    path: 'borderBlock' as const,
  },
  'border-block-color': {
    section: 'expandedCss',
    default: 'currentcolor',
    path: 'borderBlockColor' as const,
    computedFrom: ['border-block'],
  },
  'border-block-style': {
    section: 'expandedCss',
    default: 'none',
    path: 'borderBlockStyle' as const,
    computedFrom: ['border-block'],
  },
  'border-block-width': {
    section: 'expandedCss',
    default: 'medium',
    path: 'borderBlockWidth' as const,
    computedFrom: ['border-block'],
  },
  'border-block-end': {
    section: 'expandedCss',
    computed: ['border-top-width', 'border-top-style', 'border-top-color'],
    default: 'medium none currentcolor',
    path: 'borderBlockEnd' as const,
  },
  'border-block-end-color': {
    section: 'expandedCss',
    default: 'currentcolor',
    path: 'borderBlockEndColor' as const,
  },
  'border-block-end-style': {
    section: 'expandedCss',
    default: 'none',
    path: 'borderBlockEndStyle' as const,
  },
  'border-block-end-width': {
    section: 'expandedCss',
    default: 'medium',
    path: 'borderBlockEndWidth' as const,
  },
  'border-block-start': {
    section: 'expandedCss',
    computed: ['border-width', 'border-style', 'border-block-start-color'],
    default: 'medium medium medium medium none none none none canvastext',
    path: 'borderBlockStart' as const,
  },
  'border-block-start-color': {
    section: 'expandedCss',
    default: 'currentcolor',
    path: 'borderBlockStartColor' as const,
    computedFrom: ['border-block-start'],
  },
  'border-block-start-style': {
    section: 'expandedCss',
    default: 'none',
    path: 'borderBlockStartStyle' as const,
  },
  'border-block-start-width': {
    section: 'expandedCss',
    default: 'medium',
    path: 'borderBlockStartWidth' as const,
  },
  'border-bottom': {
    section: 'expandedCss',
    computed: [
      'border-bottom-width',
      'border-bottom-style',
      'border-bottom-color',
    ],
    default: 'medium none currentcolor',
    path: 'borderBottom' as const,
  },
  'border-bottom-color': {
    section: 'border',
    path: 'borderBottomColor' as const,
    color: true,
    allowSwatches: true,
    supportedVariableTypes: ['color'],
    computedFrom: ['border', 'border-bottom', 'border-color'],
  },
  'border-bottom-left-radius': {
    section: 'border',
    path: 'borderRadiusBottomLeft' as const,
    default: 0,
    units: ['%', 'px'],
    defaultUnit: 'px',
    placeholder: '0',
    min: 0,
    max: 1000,
    dimensional: true,
    supportedVariableTypes: ['length', 'percentage'],
    computedFrom: ['border-radius'],
  },
  'border-bottom-right-radius': {
    section: 'border',
    path: 'borderRadiusBottomRight' as const,
    default: 0,
    units: ['%', 'px'],
    defaultUnit: 'px',
    placeholder: '0',
    min: 0,
    max: 1000,
    dimensional: true,
    supportedVariableTypes: ['length', 'percentage'],
    computedFrom: ['border-radius'],
  },
  'border-bottom-style': {
    section: 'border',
    path: 'borderBottomStyle' as const,
    default: 'none',
    computedFrom: [
      'border',
      'border-block-start',
      'border-inline-end',
      'border-inline-start',
      'border-bottom',
      'border-style',
    ],
  },
  'border-bottom-width': {
    section: 'border',
    path: 'borderBottomWidth' as const,
    default: 0,
    units: ['%', 'px'],
    defaultUnit: 'px',
    min: 0,
    max: 1000,
    dimensional: true,
    supportedVariableTypes: ['length'],
    computedFrom: [
      'border',
      'border-block-start',
      'border-inline-end',
      'border-inline-start',
      'border-bottom',
      'border-width',
    ],
  },
  'border-collapse': {
    section: 'expandedCss',
    default: 'separate',
    path: 'borderCollapse' as const,
    typographic: true,
  },
  'border-color': {
    section: 'border',
    computed: [
      'border-bottom-color',
      'border-left-color',
      'border-right-color',
      'border-top-color',
    ],
    path: 'borderColor' as const,
    color: true,
    allowSwatches: true,
    supportedVariableTypes: ['color'],
    computedFrom: ['border'],
  },
  'border-end-end-radius': {
    section: 'expandedCss',
    default: '0',
    path: 'borderEndEndRadius' as const,
  },
  'border-end-start-radius': {
    section: 'expandedCss',
    default: '0',
    path: 'borderEndStartRadius' as const,
  },
  'border-image': {
    section: 'expandedCss',
    computed: [
      'border-image-outset',
      'border-image-repeat',
      'border-image-slice',
      'border-image-source',
      'border-image-width',
    ],
    default: 'none 100% 1 0 stretch',
    path: 'borderImage' as const,
  },
  'border-image-outset': {
    section: 'expandedCss',
    default: '0',
    path: 'borderImageOutset' as const,
    computedFrom: ['border-image'],
  },
  'border-image-repeat': {
    section: 'expandedCss',
    default: 'stretch',
    path: 'borderImageRepeat' as const,
    computedFrom: ['border-image'],
  },
  'border-image-slice': {
    section: 'expandedCss',
    default: '100%',
    path: 'borderImageSlice' as const,
    computedFrom: ['border-image'],
  },
  'border-image-source': {
    section: 'expandedCss',
    default: 'none',
    path: 'borderImageSource' as const,
    computedFrom: ['border-image'],
  },
  'border-image-width': {
    section: 'expandedCss',
    default: '1',
    path: 'borderImageWidth' as const,
    computedFrom: ['border-image'],
  },
  'border-inline': {
    section: 'expandedCss',
    computed: [
      'border-inline-width',
      'border-inline-style',
      'border-inline-color',
    ],
    default: 'medium none currentcolor',
    path: 'borderInline' as const,
  },
  'border-inline-end': {
    section: 'expandedCss',
    computed: ['border-width', 'border-style', 'border-inline-end-color'],
    default: 'medium medium medium medium none none none none canvastext',
    path: 'borderInlineEnd' as const,
  },
  'border-inline-color': {
    section: 'expandedCss',
    default: 'currentcolor',
    path: 'borderInlineColor' as const,
    computedFrom: ['border-inline'],
  },
  'border-inline-style': {
    section: 'expandedCss',
    default: 'none',
    path: 'borderInlineStyle' as const,
    computedFrom: ['border-inline'],
  },
  'border-inline-width': {
    section: 'expandedCss',
    default: 'medium',
    path: 'borderInlineWidth' as const,
    computedFrom: ['border-inline'],
  },
  'border-inline-end-color': {
    section: 'expandedCss',
    default: 'currentcolor',
    path: 'borderInlineEndColor' as const,
    computedFrom: ['border-inline-end'],
  },
  'border-inline-end-style': {
    section: 'expandedCss',
    default: 'none',
    path: 'borderInlineEndStyle' as const,
  },
  'border-inline-end-width': {
    section: 'expandedCss',
    default: 'medium',
    path: 'borderInlineEndWidth' as const,
  },
  'border-inline-start': {
    section: 'expandedCss',
    computed: ['border-width', 'border-style', 'border-inline-start-color'],
    default: 'medium medium medium medium none none none none canvastext',
    path: 'borderInlineStart' as const,
  },
  'border-inline-start-color': {
    section: 'expandedCss',
    default: 'currentcolor',
    path: 'borderInlineStartColor' as const,
    computedFrom: ['border-inline-start'],
  },
  'border-inline-start-style': {
    section: 'expandedCss',
    default: 'none',
    path: 'borderInlineStartStyle' as const,
  },
  'border-inline-start-width': {
    section: 'expandedCss',
    default: 'medium',
    path: 'borderInlineStartWidth' as const,
  },
  'border-left': {
    section: 'expandedCss',
    computed: ['border-left-width', 'border-left-style', 'border-left-color'],
    default: 'medium none currentcolor',
    path: 'borderLeft' as const,
  },
  'border-left-color': {
    section: 'border',
    path: 'borderLeftColor' as const,
    color: true,
    allowSwatches: true,
    supportedVariableTypes: ['color'],
    computedFrom: ['border', 'border-color', 'border-left'],
  },
  'border-left-style': {
    section: 'border',
    path: 'borderLeftStyle' as const,
    default: 'none',
    computedFrom: [
      'border',
      'border-block-start',
      'border-inline-end',
      'border-inline-start',
      'border-left',
      'border-style',
    ],
  },
  'border-left-width': {
    section: 'border',
    path: 'borderLeftWidth' as const,
    default: 0,
    units: ['%', 'px'],
    defaultUnit: 'px',
    min: 0,
    max: 1000,
    dimensional: true,
    supportedVariableTypes: ['length'],
    computedFrom: [
      'border',
      'border-block-start',
      'border-inline-end',
      'border-inline-start',
      'border-left',
      'border-width',
    ],
  },
  'border-radius': {
    section: 'border',
    computed: [
      'border-bottom-left-radius',
      'border-bottom-right-radius',
      'border-top-left-radius',
      'border-top-right-radius',
    ],
    path: 'borderRadius' as const,
    default: 0,
    units: ['%', 'px'],
    defaultUnit: 'px',
    placeholder: '0',
    min: 0,
    max: 1000,
    dimensional: true,
    supportedVariableTypes: ['length', 'percentage'],
  },
  'border-right': {
    section: 'expandedCss',
    computed: [
      'border-right-width',
      'border-right-style',
      'border-right-color',
    ],
    default: 'medium none currentcolor',
    path: 'borderRight' as const,
  },
  'border-right-color': {
    section: 'border',
    path: 'borderRightColor' as const,
    color: true,
    allowSwatches: true,
    supportedVariableTypes: ['color'],
    computedFrom: ['border', 'border-color', 'border-right'],
  },
  'border-right-style': {
    section: 'border',
    path: 'borderRightStyle' as const,
    default: 'none',
    computedFrom: [
      'border',
      'border-block-start',
      'border-inline-end',
      'border-inline-start',
      'border-right',
      'border-style',
    ],
  },
  'border-right-width': {
    section: 'border',
    path: 'borderRightWidth' as const,
    default: 0,
    units: ['%', 'px'],
    defaultUnit: 'px',
    min: 0,
    max: 1000,
    dimensional: true,
    supportedVariableTypes: ['length'],
    computedFrom: [
      'border',
      'border-block-start',
      'border-inline-end',
      'border-inline-start',
      'border-right',
      'border-width',
    ],
  },
  'border-spacing': {
    section: 'expandedCss',
    default: '0',
    path: 'borderSpacing' as const,
    typographic: true,
  },
  'border-start-end-radius': {
    section: 'expandedCss',
    default: '0',
    path: 'borderStartEndRadius' as const,
  },
  'border-start-start-radius': {
    section: 'expandedCss',
    default: '0',
    path: 'borderStartStartRadius' as const,
  },
  'border-style': {
    section: 'border',
    computed: [
      'border-bottom-style',
      'border-left-style',
      'border-right-style',
      'border-top-style',
    ],
    path: 'borderStyle' as const,
    computedFrom: [
      'border',
      'border-block-start',
      'border-inline-end',
      'border-inline-start',
    ],
  },
  'border-top': {
    section: 'expandedCss',
    computed: ['border-top-width', 'border-top-style', 'border-top-color'],
    default: 'medium none currentcolor',
    path: 'borderTop' as const,
  },
  'border-top-color': {
    section: 'border',
    path: 'borderTopColor' as const,
    color: true,
    allowSwatches: true,
    supportedVariableTypes: ['color'],
    computedFrom: ['border', 'border-block-end', 'border-color', 'border-top'],
  },
  'border-top-left-radius': {
    section: 'border',
    path: 'borderRadiusTopLeft' as const,
    default: 0,
    units: ['%', 'px'],
    defaultUnit: 'px',
    placeholder: '0',
    min: 0,
    max: 1000,
    dimensional: true,
    supportedVariableTypes: ['length', 'percentage'],
    computedFrom: ['border-radius'],
  },
  'border-top-right-radius': {
    section: 'border',
    path: 'borderRadiusTopRight' as const,
    default: 0,
    units: ['%', 'px'],
    defaultUnit: 'px',
    placeholder: '0',
    min: 0,
    max: 1000,
    dimensional: true,
    supportedVariableTypes: ['length', 'percentage'],
    computedFrom: ['border-radius'],
  },
  'border-top-style': {
    section: 'border',
    path: 'borderTopStyle' as const,
    default: 'none',
    computedFrom: [
      'border',
      'border-block-start',
      'border-inline-end',
      'border-inline-start',
      'border-block-end',
      'border-style',
      'border-top',
    ],
  },
  'border-top-width': {
    section: 'border',
    path: 'borderTopWidth' as const,
    default: 0,
    units: ['%', 'px'],
    defaultUnit: 'px',
    min: 0,
    max: 1000,
    dimensional: true,
    supportedVariableTypes: ['length'],
    computedFrom: [
      'border',
      'border-block-start',
      'border-inline-end',
      'border-inline-start',
      'border-block-end',
      'border-top',
      'border-width',
    ],
  },
  'border-width': {
    section: 'border',
    computed: [
      'border-bottom-width',
      'border-left-width',
      'border-right-width',
      'border-top-width',
    ],
    path: 'borderWidth' as const,
    units: ['px'],
    defaultUnit: 'px',
    min: 0,
    max: 1000,
    dimensional: true,
    supportedVariableTypes: ['length'],
    computedFrom: [
      'border',
      'border-block-start',
      'border-inline-end',
      'border-inline-start',
    ],
  },
  bottom: {
    section: 'position',
    path: 'bottom' as const,
    default: 'auto',
    units: ['%', 'px', 'auto'],
    defaultUnit: 'px',
    min: -9999,
    max: 9999,
    dimensional: true,
    supportedVariableTypes: ['length', 'percentage'],
    computedFrom: ['inset'],
  },
  'box-align': {
    section: 'expandedCss',
    default: 'stretch',
    path: 'boxAlign' as const,
  },
  'box-decoration-break': {
    section: 'expandedCss',
    default: 'slice',
    path: 'boxDecorationBreak' as const,
  },
  'box-direction': {
    section: 'expandedCss',
    default: 'normal',
    path: 'boxDirection' as const,
  },
  'box-flex': {
    section: 'expandedCss',
    default: '0',
    path: 'boxFlex' as const,
  },
  'box-flex-group': {
    section: 'expandedCss',
    default: '1',
    path: 'boxFlexGroup' as const,
  },
  'box-lines': {
    section: 'expandedCss',
    default: 'single',
    path: 'boxLines' as const,
  },
  'box-ordinal-group': {
    section: 'expandedCss',
    default: '1',
    path: 'boxOrdinalGroup' as const,
  },
  'box-orient': {
    section: 'expandedCss',
    default: 'inlineAxisHorizontalInXUL',
    path: 'boxOrient' as const,
  },
  'box-pack': {
    section: 'expandedCss',
    default: 'start',
    path: 'boxPack' as const,
  },
  'box-shadow': {
    section: 'shadows',
    path: 'boxShadows' as const,
    multi: true,
    allowSwatches: true,
    supportedVariableTypes: ['length'],
  },
  'box-sizing': {
    section: 'sizing',
    path: 'boxSizing' as const,
    default: 'border-box',
  },
  'break-after': {
    section: 'expandedCss',
    default: 'auto',
    path: 'breakAfter' as const,
  },
  'break-before': {
    section: 'expandedCss',
    default: 'auto',
    path: 'breakBefore' as const,
  },
  'break-inside': {
    section: 'expandedCss',
    default: 'auto',
    path: 'breakInside' as const,
  },
  'caption-side': {
    section: 'expandedCss',
    default: 'top',
    path: 'captionSide' as const,
    typographic: true,
  },
  caret: {
    section: 'expandedCss',
    computed: ['caret-color', 'caret-shape'],
    default: 'auto auto',
    path: 'caret' as const,
    typographic: true,
  },
  'caret-color': {
    section: 'expandedCss',
    default: 'auto',
    path: 'caretColor' as const,
    typographic: true,
    computedFrom: ['caret'],
  },
  'caret-shape': {
    section: 'expandedCss',
    default: 'auto',
    path: 'caretShape' as const,
    typographic: true,
    computedFrom: ['caret'],
  },
  clear: {
    section: 'position',
    path: 'clear' as const,
    default: 'none',
  },
  clip: {
    section: 'expandedCss',
    default: 'auto',
    path: 'clip' as const,
  },
  'clip-path': {
    section: 'expandedCss',
    default: 'none',
    path: 'clipPath' as const,
  },
  'clip-rule': {
    section: 'expandedCss',
    default: 'nonzero',
    path: 'clipRule' as const,
    typographic: true,
  },
  color: {
    section: 'typography',
    path: 'fontColor' as const,
    typographic: true,
    color: true,
    allowSwatches: true,
    supportedVariableTypes: ['color'],
    placeholder: 'transparent',
  },
  'color-interpolation-filters': {
    section: 'expandedCss',
    default: 'linearRGB',
    path: 'colorInterpolationFilters' as const,
    typographic: true,
  },
  'color-scheme': {
    section: 'expandedCss',
    default: 'normal',
    path: 'colorScheme' as const,
    typographic: true,
  },
  'column-count': {
    section: 'typography',
    path: 'columnCount' as const,
    min: 2,
    max: 6,
    placeholder: 'Auto',
    supportedVariableTypes: ['number'],
    computedFrom: ['columns'],
  },
  'column-fill': {
    section: 'expandedCss',
    default: 'balance',
    path: 'columnFill' as const,
  },
  'column-gap': {
    section: 'typography',
    path: 'columnGap' as const,
    default: 1,
    units: ['em', 'px'],
    defaultUnit: 'em',
    placeholder: '0',
    supportedVariableTypes: ['length', 'percentage'],
    computedFrom: ['gap', 'grid'],
  },
  'column-rule': {
    section: 'expandedCss',
    computed: ['column-rule-color', 'column-rule-style', 'column-rule-width'],
    default: 'medium none currentcolor',
    path: 'columnRule' as const,
  },
  'column-rule-color': {
    section: 'typography',
    path: 'columnRuleColor' as const,
    color: true,
    allowSwatches: true,
    supportedVariableTypes: ['color'],
    placeholder: 'None',
    computedFrom: ['column-rule'],
  },
  'column-rule-style': {
    section: 'typography',
    path: 'columnRuleStyle' as const,
    default: 'none',
    computedFrom: ['column-rule'],
  },
  'column-rule-width': {
    section: 'typography',
    path: 'columnRuleWidth' as const,
    default: 0,
    units: ['px'],
    defaultUnit: 'px',
    min: 0,
    max: 1000,
    dimensional: true,
    supportedVariableTypes: ['length'],
    computedFrom: ['column-rule'],
  },
  'column-span': {
    section: 'typography',
    path: 'columnSpan' as const,
    default: 'none',
  },
  'column-width': {
    section: 'expandedCss',
    default: 'auto',
    path: 'columnWidth' as const,
    computedFrom: ['columns'],
  },
  columns: {
    section: 'expandedCss',
    computed: ['column-width', 'column-count'],
    default: 'auto auto',
    path: 'columns' as const,
  },
  contain: {
    section: 'expandedCss',
    default: 'none',
    path: 'contain' as const,
  },
  'contain-intrinsic-size': {
    section: 'expandedCss',
    computed: ['contain-intrinsic-width', 'contain-intrinsic-height'],
    default: 'none none',
    path: 'containIntrinsicSize' as const,
  },
  'contain-intrinsic-block-size': {
    section: 'expandedCss',
    default: 'none',
    path: 'containIntrinsicBlockSize' as const,
  },
  'contain-intrinsic-height': {
    section: 'expandedCss',
    default: 'none',
    path: 'containIntrinsicHeight' as const,
    computedFrom: ['contain-intrinsic-size'],
  },
  'contain-intrinsic-inline-size': {
    section: 'expandedCss',
    default: 'none',
    path: 'containIntrinsicInlineSize' as const,
  },
  'contain-intrinsic-width': {
    section: 'expandedCss',
    default: 'none',
    path: 'containIntrinsicWidth' as const,
    computedFrom: ['contain-intrinsic-size'],
  },
  container: {
    section: 'expandedCss',
    computed: ['container-name', 'container-type'],
    default: 'none normal',
    path: 'container' as const,
  },
  'container-name': {
    section: 'expandedCss',
    default: 'none',
    path: 'containerName' as const,
    computedFrom: ['container'],
  },
  'container-type': {
    section: 'expandedCss',
    default: 'normal',
    path: 'containerType' as const,
    computedFrom: ['container'],
  },
  content: {
    section: 'expandedCss',
    path: 'content' as const,
    default: 'normal',
  },
  'content-visibility': {
    section: 'expandedCss',
    default: 'visible',
    path: 'contentVisibility' as const,
  },
  'counter-increment': {
    section: 'expandedCss',
    default: 'none',
    path: 'counterIncrement' as const,
  },
  'counter-reset': {
    section: 'expandedCss',
    default: 'none',
    path: 'counterReset' as const,
  },
  'counter-set': {
    section: 'expandedCss',
    default: 'none',
    path: 'counterSet' as const,
  },
  cursor: {
    section: 'effects',
    path: 'cursor' as const,
    default: 'auto',
    typographic: true,
  },
  d: {
    section: 'expandedCss',
    default: 'none',
    path: 'd' as const,
  },
  cx: {
    section: 'expandedCss',
    default: '0',
    path: 'cx' as const,
  },
  cy: {
    section: 'expandedCss',
    default: '0',
    path: 'cy' as const,
  },
  direction: {
    section: 'typography',
    path: 'direction' as const,
    typographic: true,
    default: 'ltr',
  },
  display: {
    section: 'layout',
    path: 'display' as const,
    default: 'inline',
  },
  'dominant-baseline': {
    section: 'expandedCss',
    default: 'auto',
    path: 'dominantBaseline' as const,
    typographic: true,
  },
  'empty-cells': {
    section: 'expandedCss',
    default: 'show',
    path: 'emptyCells' as const,
    typographic: true,
  },
  'field-sizing': {
    section: 'expandedCss',
    default: 'fixed',
    path: 'fieldSizing' as const,
  },
  fill: {
    section: 'expandedCss',
    default: 'black',
    path: 'fill' as const,
    typographic: true,
  },
  'fill-opacity': {
    section: 'expandedCss',
    default: '1',
    path: 'fillOpacity' as const,
    typographic: true,
  },
  'fill-rule': {
    section: 'expandedCss',
    default: 'nonzero',
    path: 'fillRule' as const,
    typographic: true,
  },
  filter: {
    section: 'effects',
    path: 'filters' as const,
    supportedVariableTypes: ['length', 'percentage'],
    multi: true,
  },
  flex: {
    section: 'expandedCss',
    computed: ['flex-grow', 'flex-shrink', 'flex-basis'],
    default: '0 1 auto',
    path: 'flex' as const,
  },
  'flex-basis': {
    section: 'layout',
    path: 'flexBasis' as const,
    default: 'auto',
    units: ['%', 'px', 'auto'],
    min: 0,
    max: 9999,
    supportedVariableTypes: ['length', 'percentage'],
    computedFrom: ['flex'],
  },
  'flex-direction': {
    section: 'layout',
    path: 'flexDirection' as const,
    default: 'row',
    computedFrom: ['flex-flow'],
  },
  'flex-flow': {
    section: 'expandedCss',
    computed: ['flex-direction', 'flex-wrap'],
    default: 'row nowrap',
    path: 'flexFlow' as const,
  },
  'flex-grow': {
    section: 'layout',
    path: 'flexGrow' as const,
    default: 0,
    supportedVariableTypes: ['number'],
    computedFrom: ['flex'],
  },
  'flex-shrink': {
    section: 'layout',
    path: 'flexShrink' as const,
    default: 1,
    supportedVariableTypes: ['number'],
    computedFrom: ['flex'],
  },
  'flex-wrap': {
    section: 'layout',
    path: 'flexWrap' as const,
    default: 'nowrap',
    computedFrom: ['flex-flow'],
  },
  float: {
    section: 'position',
    path: 'float' as const,
    default: 'none',
  },
  font: {
    section: 'expandedCss',
    computed: [
      'font-style',
      'font-variant',
      'font-weight',
      'font-stretch',
      'font-size',
      'line-height',
      'font-family',
    ],
    default: 'normal normal normal normal medium normal dependsOnUserAgent',
    path: 'font' as const,
    typographic: true,
  },
  'font-family': {
    section: 'typography',
    path: 'fontFamily' as const,
    typographic: true,
    default: 'Arial',
    supportedVariableTypes: ['font-family'],
    computedFrom: ['font'],
  },
  'font-feature-settings': {
    section: 'expandedCss',
    default: 'normal',
    path: 'fontFeatureSettings' as const,
    typographic: true,
  },
  'font-kerning': {
    section: 'expandedCss',
    default: 'auto',
    path: 'fontKerning' as const,
    typographic: true,
  },
  'font-language-override': {
    section: 'expandedCss',
    default: 'normal',
    path: 'fontLanguageOverride' as const,
    typographic: true,
  },
  'font-optical-sizing': {
    section: 'expandedCss',
    default: 'auto',
    path: 'fontOpticalSizing' as const,
    typographic: true,
  },
  'font-palette': {
    section: 'expandedCss',
    default: 'normal',
    path: 'fontPalette' as const,
    typographic: true,
  },
  'font-variation-settings': {
    section: 'typography',
    path: 'fontVariationSettings' as const,
    typographic: true,
    multi: true,
    default: 'normal',
  },
  'font-size': {
    section: 'typography',
    path: 'fontSize' as const,
    typographic: true,
    default: '',
    units: ['px', 'em', '%'],
    defaultUnit: 'px',
    dimensional: true,
    supportedVariableTypes: ['length', 'percentage'],
    computedFrom: ['font'],
  },
  'font-size-adjust': {
    section: 'expandedCss',
    default: 'none',
    path: 'fontSizeAdjust' as const,
    typographic: true,
  },
  'font-smooth': {
    section: 'expandedCss',
    default: 'auto',
    path: 'fontSmooth' as const,
    typographic: true,
  },
  'font-stretch': {
    section: 'expandedCss',
    default: 'normal',
    path: 'fontStretch' as const,
    typographic: true,
    computedFrom: ['font'],
  },
  'font-style': {
    section: 'typography',
    path: 'fontStyle' as const,
    typographic: true,
    default: 'normal',
    computedFrom: ['font'],
  },
  'font-synthesis': {
    section: 'expandedCss',
    default: 'weight style small-caps position ',
    path: 'fontSynthesis' as const,
    typographic: true,
  },
  'font-synthesis-position': {
    section: 'expandedCss',
    default: 'none',
    path: 'fontSynthesisPosition' as const,
    typographic: true,
  },
  'font-synthesis-small-caps': {
    section: 'expandedCss',
    default: 'auto',
    path: 'fontSynthesisSmallCaps' as const,
    typographic: true,
  },
  'font-synthesis-style': {
    section: 'expandedCss',
    default: 'auto',
    path: 'fontSynthesisStyle' as const,
    typographic: true,
  },
  'font-synthesis-weight': {
    section: 'expandedCss',
    default: 'auto',
    path: 'fontSynthesisWeight' as const,
    typographic: true,
  },
  'font-variant': {
    section: 'expandedCss',
    default: 'normal',
    path: 'fontVariant' as const,
    typographic: true,
    computedFrom: ['font'],
  },
  'font-variant-alternates': {
    section: 'expandedCss',
    default: 'normal',
    path: 'fontVariantAlternates' as const,
    typographic: true,
  },
  'font-variant-caps': {
    section: 'expandedCss',
    default: 'normal',
    path: 'fontVariantCaps' as const,
    typographic: true,
  },
  'font-variant-east-asian': {
    section: 'expandedCss',
    default: 'normal',
    path: 'fontVariantEastAsian' as const,
    typographic: true,
  },
  'font-variant-ligatures': {
    section: 'expandedCss',
    default: 'normal',
    path: 'fontVariantLigatures' as const,
    typographic: true,
  },
  'font-variant-numeric': {
    section: 'expandedCss',
    default: 'normal',
    path: 'fontVariantNumeric' as const,
    typographic: true,
  },
  'font-variant-position': {
    section: 'expandedCss',
    default: 'normal',
    path: 'fontVariantPosition' as const,
    typographic: true,
  },
  'font-weight': {
    section: 'typography',
    path: 'fontWeight' as const,
    typographic: true,
    transforms: {
      normal: 400,
      bold: 700,
    },
    default: '400',
    dimensional: true,
    supportedVariableTypes: ['number'],
    computedFrom: ['font'],
  },
  'forced-color-adjust': {
    section: 'expandedCss',
    default: 'auto',
    path: 'forcedColorAdjust' as const,
    typographic: true,
  },
  gap: {
    section: 'expandedCss',
    computed: ['row-gap', 'column-gap'],
    default: 'normal normal',
    path: 'gap' as const,
  },
  grid: {
    section: 'expandedCss',
    computed: [
      'grid-template-rows',
      'grid-template-columns',
      'grid-template-areas',
      'grid-auto-rows',
      'grid-auto-columns',
      'grid-auto-flow',
      'grid-column-gap',
      'grid-row-gap',
      'column-gap',
      'row-gap',
    ],
    default: 'none none none auto auto row 0 0 normal normal',
    path: 'grid' as const,
  },
  'grid-area': {
    section: 'layout',
    computed: [
      'grid-row-start',
      'grid-column-start',
      'grid-row-end',
      'grid-column-end',
    ],
    path: 'gridArea' as const,
  },
  'grid-auto-columns': {
    section: 'layout',
    path: 'gridAutoColumns' as const,
    default: 'auto',
    computedFrom: ['grid'],
  },
  'grid-auto-flow': {
    section: 'layout',
    path: 'gridAutoFlow' as const,
    computedFrom: ['grid'],
  },
  'grid-auto-rows': {
    section: 'layout',
    path: 'gridAutoRows' as const,
    default: 'auto',
    computedFrom: ['grid'],
  },
  'grid-column': {
    section: 'expandedCss',
    computed: ['grid-column-start', 'grid-column-end'],
    default: 'auto auto',
    path: 'gridColumn' as const,
  },
  'grid-column-end': {
    section: 'layout',
    path: 'gridColumnEnd' as const,
    default: 'auto',
    computedFrom: ['grid-area', 'grid-column'],
  },
  'grid-column-gap': {
    section: 'layout',
    path: 'gridColumnGap' as const,
    default: '0px',
    supportedVariableTypes: ['length', 'percentage'],
    computedFrom: ['grid', 'grid-gap'],
  },
  'grid-column-start': {
    section: 'layout',
    path: 'gridColumnStart' as const,
    default: 'auto',
    computedFrom: ['grid-area', 'grid-column'],
  },
  'grid-gap': {
    section: 'expandedCss',
    computed: ['grid-row-gap', 'grid-column-gap'],
    default: '0 0',
    path: 'gridGap' as const,
  },
  'grid-row': {
    section: 'expandedCss',
    computed: ['grid-row-start', 'grid-row-end'],
    default: 'auto auto',
    path: 'gridRow' as const,
  },
  'grid-row-end': {
    section: 'layout',
    path: 'gridRowEnd' as const,
    default: 'auto',
    computedFrom: ['grid-area', 'grid-row'],
  },
  'grid-row-gap': {
    section: 'layout',
    path: 'gridRowGap' as const,
    default: '0px',
    supportedVariableTypes: ['length', 'percentage'],
    computedFrom: ['grid', 'grid-gap'],
  },
  'grid-row-start': {
    section: 'layout',
    path: 'gridRowStart' as const,
    default: 'auto',
    computedFrom: ['grid-area', 'grid-row'],
  },
  'grid-template': {
    section: 'expandedCss',
    computed: [
      'grid-template-columns',
      'grid-template-rows',
      'grid-template-areas',
    ],
    path: 'gridTemplate' as const,
    default: 'none none none',
  },
  'grid-template-areas': {
    section: 'layout',
    path: 'gridTemplateAreas' as const,
    computedFrom: ['grid', 'grid-template'],
  },
  'grid-template-columns': {
    section: 'layout',
    path: 'gridTemplateColumns' as const,
    default: 'none',
    computedFrom: ['grid', 'grid-template'],
  },
  'grid-template-rows': {
    section: 'layout',
    path: 'gridTemplateRows' as const,
    default: 'none',
    computedFrom: ['grid', 'grid-template'],
  },
  'hanging-punctuation': {
    section: 'expandedCss',
    default: 'none',
    path: 'hangingPunctuation' as const,
    typographic: true,
  },
  height: {
    section: 'sizing',
    path: 'height' as const,
    default: 'auto',
    units: ['%', 'px', 'auto'],
    defaultUnit: 'px',
    min: 0,
    max: 9999,
    placeholder: 'Auto',
    dimensional: true,
    supportedVariableTypes: ['length', 'percentage'],
  },
  'hyphenate-character': {
    section: 'expandedCss',
    default: 'auto',
    path: 'hyphenateCharacter' as const,
    typographic: true,
  },
  'hyphenate-limit-chars': {
    section: 'expandedCss',
    default: 'auto',
    path: 'hyphenateLimitChars' as const,
    typographic: true,
  },
  hyphens: {
    section: 'expandedCss',
    default: 'manual',
    path: 'hyphens' as const,
    typographic: true,
  },
  'image-orientation': {
    section: 'expandedCss',
    default: 'from-image',
    path: 'imageOrientation' as const,
    typographic: true,
  },
  'image-rendering': {
    section: 'expandedCss',
    default: 'auto',
    path: 'imageRendering' as const,
    typographic: true,
  },
  'image-resolution': {
    section: 'expandedCss',
    default: '1dppx',
    path: 'imageResolution' as const,
    typographic: true,
  },
  'ime-mode': {
    section: 'expandedCss',
    default: 'auto',
    path: 'imeMode' as const,
  },
  'initial-letter': {
    section: 'expandedCss',
    default: 'normal',
    path: 'initialLetter' as const,
  },
  'initial-letter-align': {
    section: 'expandedCss',
    default: 'auto',
    path: 'initialLetterAlign' as const,
  },
  'inline-size': {
    section: 'expandedCss',
    default: 'auto',
    path: 'inlineSize' as const,
  },
  'input-security': {
    section: 'expandedCss',
    default: 'auto',
    path: 'inputSecurity' as const,
  },
  inset: {
    section: 'expandedCss',
    computed: ['top', 'bottom', 'left', 'right'],
    default: 'auto auto auto auto',
    path: 'inset' as const,
  },
  'inset-block': {
    section: 'expandedCss',
    computed: ['inset-block-start', 'inset-block-end'],
    default: 'auto auto',
    path: 'insetBlock' as const,
  },
  'inset-block-end': {
    section: 'expandedCss',
    default: 'auto',
    path: 'insetBlockEnd' as const,
    computedFrom: ['inset-block'],
  },
  'inset-block-start': {
    section: 'expandedCss',
    default: 'auto',
    path: 'insetBlockStart' as const,
    computedFrom: ['inset-block'],
  },
  'inset-inline': {
    section: 'expandedCss',
    computed: ['inset-inline-start', 'inset-inline-end'],
    default: 'auto auto',
    path: 'insetInline' as const,
  },
  'inset-inline-end': {
    section: 'expandedCss',
    default: 'auto',
    path: 'insetInlineEnd' as const,
    computedFrom: ['inset-inline'],
  },
  'inset-inline-start': {
    section: 'expandedCss',
    default: 'auto',
    path: 'insetInlineStart' as const,
    computedFrom: ['inset-inline'],
  },
  'interpolate-size': {
    section: 'expandedCss',
    default: 'numeric-only',
    path: 'interpolateSize' as const,
    typographic: true,
  },
  isolation: {
    section: 'expandedCss',
    default: 'auto',
    path: 'isolation' as const,
  },
  'justify-content': {
    section: 'layout',
    path: 'justifyContent' as const,
    default: 'flex-start',
    computedFrom: ['place-content'],
  },
  'justify-items': {
    section: 'layout',
    path: 'justifyItems' as const,
    default: 'stretch',
    computedFrom: ['place-items'],
  },
  'justify-self': {
    section: 'layout',
    path: 'justifySelf' as const,
    default: 'auto',
    computedFrom: ['place-self'],
  },
  'justify-tracks': {
    section: 'expandedCss',
    default: 'normal',
    path: 'justifyTracks' as const,
  },
  left: {
    section: 'position',
    path: 'left' as const,
    default: 'auto',
    units: ['%', 'px', 'auto'],
    defaultUnit: 'px',
    min: -9999,
    max: 9999,
    dimensional: true,
    supportedVariableTypes: ['length', 'percentage'],
    computedFrom: ['inset'],
  },
  'letter-spacing': {
    section: 'typography',
    path: 'letterSpacing' as const,
    typographic: true,
    units: ['px', 'em', '%'],
    defaultUnit: 'px',
    min: 0,
    max: 500,
    dimensional: true,
    supportedVariableTypes: ['length'],
  },
  'line-break': {
    section: 'expandedCss',
    default: 'auto',
    path: 'lineBreak' as const,
    typographic: true,
  },
  'line-height': {
    section: 'typography',
    path: 'lineHeight' as const,
    typographic: true,
    transforms: {
      normal: 1.2,
    },
    default: '',
    units: ['-', 'px', 'em', '%'],
    defaultUnit: '-',
    min: 0,
    max: 500,
    dimensional: true,
    supportedVariableTypes: ['length', 'percentage', 'number'],
    computedFrom: ['font'],
  },
  'line-height-step': {
    section: 'expandedCss',
    path: 'lineHeightStep' as const,
    default: '0',
    supportedVariableTypes: ['length'],
    typographic: true,
  },
  'list-style': {
    section: 'expandedCss',
    computed: ['list-style-image', 'list-style-position', 'list-style-type'],
    default: 'disc outside none',
    path: 'listStyle' as const,
    typographic: true,
  },
  'list-style-image': {
    section: 'expandedCss',
    default: 'none',
    path: 'listStyleImage' as const,
    typographic: true,
    computedFrom: ['list-style'],
  },
  'list-style-position': {
    section: 'expandedCss',
    default: 'outside',
    path: 'listStylePosition' as const,
    typographic: true,
    computedFrom: ['list-style'],
  },
  'list-style-type': {
    section: 'typography',
    path: 'listStyleType' as const,
    typographic: true,
    default: 'disc',
    computedFrom: ['list-style'],
  },
  margin: {
    section: 'expandedCss',
    computed: ['margin-bottom', 'margin-left', 'margin-right', 'margin-top'],
    default: '0 0 0 0',
    path: 'margin' as const,
  },
  'margin-block': {
    section: 'expandedCss',
    computed: ['margin-block-start', 'margin-block-end'],
    default: '0 0',
    path: 'marginBlock' as const,
  },
  'margin-block-end': {
    section: 'expandedCss',
    default: '0',
    path: 'marginBlockEnd' as const,
    computedFrom: ['margin-block'],
  },
  'margin-block-start': {
    section: 'expandedCss',
    default: '0',
    path: 'marginBlockStart' as const,
    computedFrom: ['margin-block'],
  },
  'margin-bottom': {
    section: 'spacing',
    path: 'marginBottom' as const,
    units: ['%', 'px', 'auto'],
    defaultUnit: 'px',
    min: -1000,
    max: 1000,
    dimensional: true,
    supportedVariableTypes: ['length', 'percentage'],
    computedFrom: ['margin'],
  },
  'margin-inline': {
    section: 'expandedCss',
    computed: ['margin-inline-start', 'margin-inline-end'],
    default: '0 0',
    path: 'marginInline' as const,
  },
  'margin-inline-end': {
    section: 'expandedCss',
    default: '0',
    path: 'marginInlineEnd' as const,
    computedFrom: ['margin-inline'],
  },
  'margin-inline-start': {
    section: 'expandedCss',
    default: '0',
    path: 'marginInlineStart' as const,
    computedFrom: ['margin-inline'],
  },
  'margin-left': {
    section: 'spacing',
    path: 'marginLeft' as const,
    units: ['%', 'px', 'auto'],
    defaultUnit: 'px',
    min: -1000,
    max: 1000,
    dimensional: true,
    supportedVariableTypes: ['length', 'percentage'],
    computedFrom: ['margin'],
  },
  'margin-right': {
    section: 'spacing',
    path: 'marginRight' as const,
    units: ['%', 'px', 'auto'],
    defaultUnit: 'px',
    min: -1000,
    max: 1000,
    dimensional: true,
    supportedVariableTypes: ['length', 'percentage'],
    computedFrom: ['margin'],
  },
  'margin-top': {
    section: 'spacing',
    path: 'marginTop' as const,
    units: ['%', 'px', 'auto'],
    defaultUnit: 'px',
    min: -1000,
    max: 1000,
    dimensional: true,
    supportedVariableTypes: ['length', 'percentage'],
    computedFrom: ['margin'],
  },
  'margin-trim': {
    section: 'expandedCss',
    default: 'none',
    path: 'marginTrim' as const,
  },
  marker: {
    section: 'expandedCss',
    default: 'none none none',
    path: 'marker' as const,
    typographic: true,
  },
  'marker-end': {
    section: 'expandedCss',
    default: 'none',
    path: 'markerEnd' as const,
    typographic: true,
  },
  'marker-mid': {
    section: 'expandedCss',
    default: 'none',
    path: 'markerMid' as const,
    typographic: true,
  },
  'marker-start': {
    section: 'expandedCss',
    default: 'none',
    path: 'markerStart' as const,
    typographic: true,
  },
  mask: {
    section: 'expandedCss',
    computed: [
      'mask-image',
      'mask-mode',
      'mask-repeat',
      'mask-position',
      'mask-clip',
      'mask-origin',
      'mask-size',
      'mask-composite',
    ],
    default: 'none match-source repeat 0% 0% border-box border-box auto add',
    path: 'mask' as const,
  },
  'mask-border': {
    section: 'expandedCss',
    computed: [
      'mask-border-mode',
      'mask-border-outset',
      'mask-border-repeat',
      'mask-border-slice',
      'mask-border-source',
      'mask-border-width',
    ],
    default: 'alpha 0 stretch 0 none auto',
    path: 'maskBorder' as const,
  },
  'mask-border-mode': {
    section: 'expandedCss',
    default: 'alpha',
    path: 'maskBorderMode' as const,
    computedFrom: ['mask-border'],
  },
  'mask-border-outset': {
    section: 'expandedCss',
    default: '0',
    path: 'maskBorderOutset' as const,
    computedFrom: ['mask-border'],
  },
  'mask-border-repeat': {
    section: 'expandedCss',
    default: 'stretch',
    path: 'maskBorderRepeat' as const,
    computedFrom: ['mask-border'],
  },
  'mask-border-slice': {
    section: 'expandedCss',
    default: '0',
    path: 'maskBorderSlice' as const,
    computedFrom: ['mask-border'],
  },
  'mask-border-source': {
    section: 'expandedCss',
    default: 'none',
    path: 'maskBorderSource' as const,
    computedFrom: ['mask-border'],
  },
  'mask-border-width': {
    section: 'expandedCss',
    default: 'auto',
    path: 'maskBorderWidth' as const,
    computedFrom: ['mask-border'],
  },
  'mask-clip': {
    section: 'expandedCss',
    default: 'border-box',
    path: 'maskClip' as const,
    computedFrom: ['mask'],
  },
  'mask-composite': {
    section: 'expandedCss',
    default: 'add',
    path: 'maskComposite' as const,
    computedFrom: ['mask'],
  },
  'mask-image': {
    section: 'expandedCss',
    default: 'none',
    path: 'maskImage' as const,
    computedFrom: ['mask'],
  },
  'mask-mode': {
    section: 'expandedCss',
    default: 'match-source',
    path: 'maskMode' as const,
    computedFrom: ['mask'],
  },
  'mask-origin': {
    section: 'expandedCss',
    default: 'border-box',
    path: 'maskOrigin' as const,
    computedFrom: ['mask'],
  },
  'mask-position': {
    section: 'expandedCss',
    default: '0% 0%',
    path: 'maskPosition' as const,
    computedFrom: ['mask'],
  },
  'mask-repeat': {
    section: 'expandedCss',
    default: 'repeat',
    path: 'maskRepeat' as const,
    computedFrom: ['mask'],
  },
  'mask-size': {
    section: 'expandedCss',
    default: 'auto',
    path: 'maskSize' as const,
    computedFrom: ['mask'],
  },
  'mask-type': {
    section: 'expandedCss',
    default: 'luminance',
    path: 'maskType' as const,
  },
  'masonry-auto-flow': {
    section: 'expandedCss',
    default: 'pack',
    path: 'masonryAutoFlow' as const,
  },
  'math-depth': {
    section: 'expandedCss',
    default: '0',
    path: 'mathDepth' as const,
    typographic: true,
  },
  'math-shift': {
    section: 'expandedCss',
    default: 'normal',
    path: 'mathShift' as const,
    typographic: true,
  },
  'math-style': {
    section: 'expandedCss',
    default: 'normal',
    path: 'mathStyle' as const,
    typographic: true,
  },
  'max-block-size': {
    section: 'expandedCss',
    default: 'none',
    path: 'maxBlockSize' as const,
  },
  'max-height': {
    section: 'sizing',
    path: 'maxHeight' as const,
    default: 'auto',
    units: ['%', 'px', 'auto'],
    defaultUnit: 'px',
    placeholder: 'none',
    min: 0,
    max: 9999,
    dimensional: true,
    supportedVariableTypes: ['length', 'percentage'],
  },
  'max-inline-size': {
    section: 'expandedCss',
    default: 'none',
    path: 'maxInlineSize' as const,
  },
  'max-lines': {
    section: 'expandedCss',
    default: 'none',
    path: 'maxLines' as const,
  },
  'max-width': {
    section: 'sizing',
    path: 'maxWidth' as const,
    default: 'auto',
    units: ['%', 'px', 'auto'],
    defaultUnit: 'px',
    placeholder: 'none',
    min: 0,
    max: 9999,
    dimensional: true,
    supportedVariableTypes: ['length', 'percentage'],
  },
  'min-block-size': {
    section: 'expandedCss',
    default: '0',
    path: 'minBlockSize' as const,
  },
  'min-height': {
    section: 'sizing',
    path: 'minHeight' as const,
    default: 0,
    units: ['%', 'px', 'auto'],
    defaultUnit: 'px',
    placeholder: '0',
    placeholderUnit: 'px',
    min: 0,
    max: 9999,
    dimensional: true,
    supportedVariableTypes: ['length', 'percentage'],
  },
  'min-inline-size': {
    section: 'expandedCss',
    default: '0',
    path: 'minInlineSize' as const,
  },
  'min-width': {
    section: 'sizing',
    path: 'minWidth' as const,
    default: 0,
    units: ['%', 'px', 'auto'],
    defaultUnit: 'px',
    placeholder: '0',
    placeholderUnit: 'px',
    min: 0,
    max: 9999,
    dimensional: true,
    supportedVariableTypes: ['length', 'percentage'],
  },
  'mix-blend-mode': {
    section: 'effects',
    path: 'mixBlendMode' as const,
    default: 'normal',
  },
  'object-fit': {
    section: 'sizing',
    path: 'objectFit' as const,
    default: 'fill',
  },
  'object-position': {
    section: 'sizing',
    path: 'objectPosition' as const,
    default: '50% 50%',
    typographic: true,
  },
  offset: {
    section: 'expandedCss',
    computed: [
      'offset-position',
      'offset-path',
      'offset-distance',
      'offset-anchor',
      'offset-rotate',
    ],
    default: 'normal none 0 auto auto',
    path: 'offset' as const,
  },
  'offset-anchor': {
    section: 'expandedCss',
    default: 'auto',
    path: 'offsetAnchor' as const,
    computedFrom: ['offset'],
  },
  'offset-distance': {
    section: 'expandedCss',
    default: '0',
    path: 'offsetDistance' as const,
    computedFrom: ['offset'],
  },
  'offset-path': {
    section: 'expandedCss',
    default: 'none',
    path: 'offsetPath' as const,
    computedFrom: ['offset'],
  },
  'offset-position': {
    section: 'expandedCss',
    default: 'normal',
    path: 'offsetPosition' as const,
    computedFrom: ['offset'],
  },
  'offset-rotate': {
    section: 'expandedCss',
    default: 'auto',
    path: 'offsetRotate' as const,
    computedFrom: ['offset'],
  },
  opacity: {
    section: 'effects',
    path: 'opacity' as const,
    default: 1,
    units: ['%'],
    defaultUnit: '%',
    placeholder: 1,
    placeholderUnit: '%',
    forceUnit: '%',
    supportedVariableTypes: ['number', 'percentage'],
  },
  order: {
    section: 'layout',
    path: 'order' as const,
    default: 0,
  },
  orphans: {
    section: 'expandedCss',
    default: '2',
    path: 'orphans' as const,
    typographic: true,
  },
  outline: {
    section: 'expandedCss',
    computed: ['outline-width', 'outline-style', 'outline-color'],
    default: 'medium none auto',
    path: 'outline' as const,
  },
  'outline-color': {
    section: 'effects',
    path: 'outlineColor' as const,
    color: true,
    allowSwatches: true,
    supportedVariableTypes: ['color'],
    computedFrom: ['outline'],
  },
  'outline-offset': {
    section: 'effects',
    path: 'outlineOffset' as const,
    units: ['px'],
    default: 0,
    defaultUnit: 'px',
    min: 0,
    max: 1000,
    dimensional: true,
  },
  'outline-style': {
    section: 'effects',
    path: 'outlineStyle' as const,
    default: 'none',
    computedFrom: ['outline'],
  },
  'outline-width': {
    section: 'effects',
    path: 'outlineWidth' as const,
    units: ['px'],
    default: 3,
    defaultUnit: 'px',
    min: 0,
    max: 1000,
    dimensional: true,
    computedFrom: ['outline'],
  },
  overflow: {
    section: 'sizing',
    computed: ['overflow-x', 'overflow-y'],
    path: 'overflow' as const,
    default: 'visible',
  },
  'overflow-anchor': {
    section: 'expandedCss',
    default: 'auto',
    path: 'overflowAnchor' as const,
  },
  'overflow-block': {
    section: 'expandedCss',
    default: 'auto',
    path: 'overflowBlock' as const,
  },
  'overflow-clip-box': {
    section: 'expandedCss',
    default: 'padding-box',
    path: 'overflowClipBox' as const,
  },
  'overflow-clip-margin': {
    section: 'expandedCss',
    default: '0px',
    path: 'overflowClipMargin' as const,
  },
  'overflow-inline': {
    section: 'expandedCss',
    default: 'auto',
    path: 'overflowInline' as const,
  },
  'overflow-wrap': {
    section: 'typography',
    path: 'overflowWrap' as const,
    typographic: true,
    default: 'normal',
  },
  'overflow-x': {
    section: 'layout',
    path: 'overflowX' as const,
    default: 'visible',
    computedFrom: ['overflow'],
  },
  'overflow-y': {
    section: 'layout',
    path: 'overflowY' as const,
    default: 'visible',
    computedFrom: ['overflow'],
  },
  overlay: {
    section: 'expandedCss',
    default: 'none',
    path: 'overlay' as const,
  },
  'overscroll-behavior': {
    section: 'expandedCss',
    computed: ['overscroll-behavior-x', 'overscroll-behavior-y'],
    default: 'auto',
    path: 'overscrollBehavior' as const,
  },
  'overscroll-behavior-block': {
    section: 'expandedCss',
    default: 'auto',
    path: 'overscrollBehaviorBlock' as const,
  },
  'overscroll-behavior-inline': {
    section: 'expandedCss',
    default: 'auto',
    path: 'overscrollBehaviorInline' as const,
  },
  'overscroll-behavior-x': {
    section: 'expandedCss',
    default: 'auto',
    path: 'overscrollBehaviorX' as const,
    computedFrom: ['overscroll-behavior'],
  },
  'overscroll-behavior-y': {
    section: 'expandedCss',
    default: 'auto',
    path: 'overscrollBehaviorY' as const,
    computedFrom: ['overscroll-behavior'],
  },
  padding: {
    section: 'expandedCss',
    computed: [
      'padding-bottom',
      'padding-left',
      'padding-right',
      'padding-top',
    ],
    default: '0 0 0 0',
    path: 'padding' as const,
  },
  'padding-block': {
    section: 'expandedCss',
    computed: ['padding-block-start', 'padding-block-end'],
    default: '0 0',
    path: 'paddingBlock' as const,
  },
  'padding-block-end': {
    section: 'expandedCss',
    default: '0',
    path: 'paddingBlockEnd' as const,
    computedFrom: ['padding-block'],
  },
  'padding-block-start': {
    section: 'expandedCss',
    default: '0',
    path: 'paddingBlockStart' as const,
    computedFrom: ['padding-block'],
  },
  'padding-bottom': {
    section: 'spacing',
    path: 'paddingBottom' as const,
    units: ['%', 'px'],
    defaultUnit: 'px',
    dimensional: true,
    supportedVariableTypes: ['length', 'percentage'],
    computedFrom: ['padding'],
  },
  'padding-inline': {
    section: 'expandedCss',
    computed: ['padding-inline-start', 'padding-inline-end'],
    default: '0 0',
    path: 'paddingInline' as const,
  },
  'padding-inline-end': {
    section: 'expandedCss',
    default: '0',
    path: 'paddingInlineEnd' as const,
    computedFrom: ['padding-inline'],
  },
  'padding-inline-start': {
    section: 'expandedCss',
    default: '0',
    path: 'paddingInlineStart' as const,
    computedFrom: ['padding-inline'],
  },
  'padding-left': {
    section: 'spacing',
    path: 'paddingLeft' as const,
    units: ['%', 'px'],
    defaultUnit: 'px',
    min: 0,
    max: 1000,
    dimensional: true,
    supportedVariableTypes: ['length', 'percentage'],
    computedFrom: ['padding'],
  },
  'padding-right': {
    section: 'spacing',
    path: 'paddingRight' as const,
    units: ['%', 'px'],
    defaultUnit: 'px',
    min: 0,
    max: 1000,
    dimensional: true,
    supportedVariableTypes: ['length', 'percentage'],
    computedFrom: ['padding'],
  },
  'padding-top': {
    section: 'spacing',
    path: 'paddingTop' as const,
    units: ['%', 'px'],
    defaultUnit: 'px',
    min: 0,
    max: 1000,
    dimensional: true,
    supportedVariableTypes: ['length', 'percentage'],
    computedFrom: ['padding'],
  },
  page: {
    section: 'expandedCss',
    default: 'auto',
    path: 'page' as const,
  },
  'page-break-after': {
    section: 'expandedCss',
    default: 'auto',
    path: 'pageBreakAfter' as const,
  },
  'page-break-before': {
    section: 'expandedCss',
    default: 'auto',
    path: 'pageBreakBefore' as const,
  },
  'page-break-inside': {
    section: 'expandedCss',
    default: 'auto',
    path: 'pageBreakInside' as const,
  },
  'paint-order': {
    section: 'expandedCss',
    default: 'normal',
    path: 'paintOrder' as const,
    typographic: true,
  },
  perspective: {
    section: 'transitionsAndTransforms',
    path: 'perspective' as const,
    units: ['px'],
    defaultUnit: 'px',
    default: 'none',
    min: -9999,
    max: 9999,
  },
  'perspective-origin': {
    section: 'transitionsAndTransforms',
    path: 'perspectiveOrigin' as const,
    default: '50% 50% 0',
    units: ['%', 'px'],
  },
  'place-content': {
    section: 'expandedCss',
    computed: ['align-content', 'justify-content'],
    default: 'normal normal',
    path: 'placeContent' as const,
  },
  'place-items': {
    section: 'expandedCss',
    computed: ['align-items', 'justify-items'],
    default: 'normal legacy',
    path: 'placeItems' as const,
  },
  'place-self': {
    section: 'expandedCss',
    computed: ['align-self', 'justify-self'],
    default: 'auto auto',
    path: 'placeSelf' as const,
  },
  'pointer-events': {
    section: 'effects',
    path: 'pointerEvents' as const,
    default: 'auto',
    typographic: true,
  },
  position: {
    section: 'position',
    path: 'position' as const,
    default: 'static',
  },
  'position-anchor': {
    section: 'expandedCss',
    default: 'auto',
    path: 'positionAnchor' as const,
  },
  'position-area': {
    section: 'expandedCss',
    default: 'none',
    path: 'positionArea' as const,
  },
  'position-try': {
    section: 'expandedCss',
    computed: ['position-try-fallbacks', 'position-try-order'],
    default: 'none normal',
    path: 'positionTry' as const,
  },
  'position-try-fallbacks': {
    section: 'expandedCss',
    default: 'none',
    path: 'positionTryFallbacks' as const,
    computedFrom: ['position-try'],
  },
  'position-try-order': {
    section: 'expandedCss',
    default: 'normal',
    path: 'positionTryOrder' as const,
    computedFrom: ['position-try'],
  },
  'position-visibility': {
    section: 'expandedCss',
    default: 'anchors-visible',
    path: 'positionVisibility' as const,
  },
  'print-color-adjust': {
    section: 'expandedCss',
    default: 'economy',
    path: 'printColorAdjust' as const,
    typographic: true,
  },
  quotes: {
    section: 'expandedCss',
    default: 'dependsOnUserAgent',
    path: 'quotes' as const,
    typographic: true,
  },
  r: {
    section: 'expandedCss',
    default: '0',
    path: 'r' as const,
  },
  resize: {
    section: 'expandedCss',
    default: 'none',
    path: 'resize' as const,
  },
  right: {
    section: 'position',
    path: 'right' as const,
    default: 'auto',
    units: ['%', 'px', 'auto'],
    defaultUnit: 'px',
    min: -9999,
    max: 9999,
    dimensional: true,
    supportedVariableTypes: ['length', 'percentage'],
    computedFrom: ['inset'],
  },
  rotate: {
    section: 'expandedCss',
    default: 'none',
    path: 'rotate' as const,
  },
  'row-gap': {
    section: 'expandedCss',
    default: 'normal',
    path: 'rowGap' as const,
    computedFrom: ['gap', 'grid'],
  },
  'ruby-align': {
    section: 'expandedCss',
    default: 'space-around',
    path: 'rubyAlign' as const,
    typographic: true,
  },
  'ruby-merge': {
    section: 'expandedCss',
    default: 'separate',
    path: 'rubyMerge' as const,
    typographic: true,
  },
  'ruby-position': {
    section: 'expandedCss',
    default: 'alternate',
    path: 'rubyPosition' as const,
    typographic: true,
  },
  rx: {
    section: 'expandedCss',
    default: '0',
    path: 'rx' as const,
  },
  ry: {
    section: 'expandedCss',
    default: '0',
    path: 'ry' as const,
  },
  scale: {
    section: 'expandedCss',
    default: 'none',
    path: 'scale' as const,
  },
  'scrollbar-color': {
    section: 'expandedCss',
    default: 'auto',
    path: 'scrollbarColor' as const,
    typographic: true,
  },
  'scrollbar-gutter': {
    section: 'expandedCss',
    default: 'auto',
    path: 'scrollbarGutter' as const,
  },
  'scrollbar-width': {
    section: 'expandedCss',
    default: 'auto',
    path: 'scrollbarWidth' as const,
  },
  'scroll-behavior': {
    section: 'expandedCss',
    default: 'auto',
    path: 'scrollBehavior' as const,
  },
  'scroll-margin': {
    section: 'expandedCss',
    computed: [
      'scroll-margin-bottom',
      'scroll-margin-left',
      'scroll-margin-right',
      'scroll-margin-top',
    ],
    default: '0 0 0 0',
    path: 'scrollMargin' as const,
  },
  'scroll-margin-block': {
    section: 'expandedCss',
    computed: ['scroll-margin-block-start', 'scroll-margin-block-end'],
    default: '0 0',
    path: 'scrollMarginBlock' as const,
  },
  'scroll-margin-block-start': {
    section: 'expandedCss',
    default: '0',
    path: 'scrollMarginBlockStart' as const,
    computedFrom: ['scroll-margin-block'],
  },
  'scroll-margin-block-end': {
    section: 'expandedCss',
    default: '0',
    path: 'scrollMarginBlockEnd' as const,
    computedFrom: ['scroll-margin-block'],
  },
  'scroll-margin-bottom': {
    section: 'expandedCss',
    default: '0',
    path: 'scrollMarginBottom' as const,
    computedFrom: ['scroll-margin'],
  },
  'scroll-margin-inline': {
    section: 'expandedCss',
    computed: ['scroll-margin-inline-start', 'scroll-margin-inline-end'],
    default: '0 0',
    path: 'scrollMarginInline' as const,
  },
  'scroll-margin-inline-start': {
    section: 'expandedCss',
    default: '0',
    path: 'scrollMarginInlineStart' as const,
    computedFrom: ['scroll-margin-inline'],
  },
  'scroll-margin-inline-end': {
    section: 'expandedCss',
    default: '0',
    path: 'scrollMarginInlineEnd' as const,
    computedFrom: ['scroll-margin-inline'],
  },
  'scroll-margin-left': {
    section: 'expandedCss',
    default: '0',
    path: 'scrollMarginLeft' as const,
    computedFrom: ['scroll-margin'],
  },
  'scroll-margin-right': {
    section: 'expandedCss',
    default: '0',
    path: 'scrollMarginRight' as const,
    computedFrom: ['scroll-margin'],
  },
  'scroll-margin-top': {
    section: 'expandedCss',
    default: '0',
    path: 'scrollMarginTop' as const,
    computedFrom: ['scroll-margin'],
  },
  'scroll-padding': {
    section: 'expandedCss',
    computed: [
      'scroll-padding-bottom',
      'scroll-padding-left',
      'scroll-padding-right',
      'scroll-padding-top',
    ],
    default: 'auto auto auto auto',
    path: 'scrollPadding' as const,
  },
  'scroll-padding-block': {
    section: 'expandedCss',
    computed: ['scroll-padding-block-start', 'scroll-padding-block-end'],
    default: 'auto auto',
    path: 'scrollPaddingBlock' as const,
  },
  'scroll-padding-block-start': {
    section: 'expandedCss',
    default: 'auto',
    path: 'scrollPaddingBlockStart' as const,
    computedFrom: ['scroll-padding-block'],
  },
  'scroll-padding-block-end': {
    section: 'expandedCss',
    default: 'auto',
    path: 'scrollPaddingBlockEnd' as const,
    computedFrom: ['scroll-padding-block'],
  },
  'scroll-padding-bottom': {
    section: 'expandedCss',
    default: 'auto',
    path: 'scrollPaddingBottom' as const,
    computedFrom: ['scroll-padding'],
  },
  'scroll-padding-inline': {
    section: 'expandedCss',
    computed: ['scroll-padding-inline-start', 'scroll-padding-inline-end'],
    default: 'auto auto',
    path: 'scrollPaddingInline' as const,
  },
  'scroll-padding-inline-start': {
    section: 'expandedCss',
    default: 'auto',
    path: 'scrollPaddingInlineStart' as const,
    computedFrom: ['scroll-padding-inline'],
  },
  'scroll-padding-inline-end': {
    section: 'expandedCss',
    default: 'auto',
    path: 'scrollPaddingInlineEnd' as const,
    computedFrom: ['scroll-padding-inline'],
  },
  'scroll-padding-left': {
    section: 'expandedCss',
    default: 'auto',
    path: 'scrollPaddingLeft' as const,
    computedFrom: ['scroll-padding'],
  },
  'scroll-padding-right': {
    section: 'expandedCss',
    default: 'auto',
    path: 'scrollPaddingRight' as const,
    computedFrom: ['scroll-padding'],
  },
  'scroll-padding-top': {
    section: 'expandedCss',
    default: 'auto',
    path: 'scrollPaddingTop' as const,
    computedFrom: ['scroll-padding'],
  },
  'scroll-snap-align': {
    section: 'expandedCss',
    default: 'none',
    path: 'scrollSnapAlign' as const,
  },
  'scroll-snap-coordinate': {
    section: 'expandedCss',
    default: 'none',
    path: 'scrollSnapCoordinate' as const,
  },
  'scroll-snap-destination': {
    section: 'expandedCss',
    default: '0px 0px',
    path: 'scrollSnapDestination' as const,
  },
  'scroll-snap-points-x': {
    section: 'expandedCss',
    default: 'none',
    path: 'scrollSnapPointsX' as const,
  },
  'scroll-snap-points-y': {
    section: 'expandedCss',
    default: 'none',
    path: 'scrollSnapPointsY' as const,
  },
  'scroll-snap-stop': {
    section: 'expandedCss',
    default: 'normal',
    path: 'scrollSnapStop' as const,
  },
  'scroll-snap-type': {
    section: 'expandedCss',
    default: 'none',
    path: 'scrollSnapType' as const,
  },
  'scroll-snap-type-x': {
    section: 'expandedCss',
    default: 'none',
    path: 'scrollSnapTypeX' as const,
  },
  'scroll-snap-type-y': {
    section: 'expandedCss',
    default: 'none',
    path: 'scrollSnapTypeY' as const,
  },
  'scroll-timeline': {
    section: 'expandedCss',
    computed: ['scroll-timeline-name', 'scroll-timeline-axis'],
    default: 'none block',
    path: 'scrollTimeline' as const,
  },
  'scroll-timeline-axis': {
    section: 'expandedCss',
    default: 'block',
    path: 'scrollTimelineAxis' as const,
    computedFrom: ['scroll-timeline'],
  },
  'scroll-timeline-name': {
    section: 'expandedCss',
    default: 'none',
    path: 'scrollTimelineName' as const,
    computedFrom: ['scroll-timeline'],
  },
  'shape-image-threshold': {
    section: 'expandedCss',
    default: '0.0',
    path: 'shapeImageThreshold' as const,
  },
  'shape-margin': {
    section: 'expandedCss',
    default: '0',
    path: 'shapeMargin' as const,
  },
  'shape-outside': {
    section: 'expandedCss',
    default: 'none',
    path: 'shapeOutside' as const,
  },
  'shape-rendering': {
    section: 'expandedCss',
    default: 'auto',
    path: 'shapeRendering' as const,
  },
  stroke: {
    section: 'expandedCss',
    default: 'none 0 butt miter 4 1 1px',
    path: 'stroke' as const,
    typographic: true,
  },
  'stroke-dasharray': {
    section: 'expandedCss',
    default: 'none',
    path: 'strokeDasharray' as const,
    typographic: true,
  },
  'stroke-dashoffset': {
    section: 'expandedCss',
    default: '0',
    path: 'strokeDashoffset' as const,
    typographic: true,
  },
  'stroke-linecap': {
    section: 'expandedCss',
    default: 'butt',
    path: 'strokeLinecap' as const,
    typographic: true,
  },
  'stroke-linejoin': {
    section: 'expandedCss',
    default: 'miter',
    path: 'strokeLinejoin' as const,
    typographic: true,
  },
  'stroke-miterlimit': {
    section: 'expandedCss',
    default: '4',
    path: 'strokeMiterlimit' as const,
    typographic: true,
  },
  'stroke-opacity': {
    section: 'expandedCss',
    default: '1',
    path: 'strokeOpacity' as const,
    typographic: true,
  },
  'stroke-width': {
    section: 'expandedCss',
    default: '1px',
    path: 'strokeWidth' as const,
    typographic: true,
  },
  'tab-size': {
    section: 'expandedCss',
    default: '8',
    path: 'tabSize' as const,
    typographic: true,
  },
  'table-layout': {
    section: 'expandedCss',
    default: 'auto',
    path: 'tableLayout' as const,
  },
  'text-align': {
    section: 'typography',
    path: 'textAlign' as const,
    typographic: true,
    default: 'left',
  },
  'text-align-last': {
    section: 'expandedCss',
    default: 'auto',
    path: 'textAlignLast' as const,
    typographic: true,
  },
  'text-anchor': {
    section: 'expandedCss',
    default: 'start',
    path: 'textAnchor' as const,
    typographic: true,
  },
  'text-combine-upright': {
    section: 'expandedCss',
    default: 'none',
    path: 'textCombineUpright' as const,
    typographic: true,
  },
  'text-decoration': {
    section: 'typography',
    computed: [
      'text-decoration-line',
      'text-decoration-style',
      'text-decoration-color',
      'text-decoration-thickness',
    ],
    path: 'textDecoration' as const,
    typographic: true,
    default: 'none',
  },
  'text-decoration-color': {
    section: 'typography',
    path: 'textDecorationColor' as const,
    typographic: true,
    supportedVariableTypes: ['color'],
    color: true,
    default: 'currentcolor',
    computedFrom: ['text-decoration'],
  },
  'text-decoration-line': {
    section: 'expandedCss',
    default: 'none',
    path: 'textDecorationLine' as const,
    computedFrom: ['text-decoration'],
  },
  'text-decoration-skip': {
    section: 'expandedCss',
    default: 'objects',
    path: 'textDecorationSkip' as const,
    typographic: true,
  },
  'text-decoration-skip-ink': {
    section: 'typography',
    path: 'textDecorationSkipInk' as const,
    typographic: true,
    default: 'auto',
  },
  'text-decoration-style': {
    section: 'typography',
    path: 'textDecorationStyle' as const,
    typographic: true,
    default: 'solid',
    computedFrom: ['text-decoration'],
  },
  'text-decoration-thickness': {
    section: 'typography',
    path: 'textDecorationThickness' as const,
    supportedVariableTypes: ['length', 'percentage'],
    typographic: true,
    default: 'auto',
    computedFrom: ['text-decoration'],
  },
  'text-emphasis': {
    section: 'expandedCss',
    computed: ['text-emphasis-style', 'text-emphasis-color'],
    default: 'none currentcolor',
    path: 'textEmphasis' as const,
    typographic: true,
  },
  'text-emphasis-color': {
    section: 'expandedCss',
    default: 'currentcolor',
    path: 'textEmphasisColor' as const,
    typographic: true,
    computedFrom: ['text-emphasis'],
  },
  'text-emphasis-position': {
    section: 'expandedCss',
    default: 'auto',
    path: 'textEmphasisPosition' as const,
    typographic: true,
  },
  'text-emphasis-style': {
    section: 'expandedCss',
    default: 'none',
    path: 'textEmphasisStyle' as const,
    typographic: true,
    computedFrom: ['text-emphasis'],
  },
  'text-indent': {
    section: 'typography',
    path: 'textIndent' as const,
    typographic: true,
    units: ['px', 'em', '%'],
    defaultUnit: 'px',
    min: 0,
    max: 500,
    dimensional: true,
    supportedVariableTypes: ['length', 'percentage'],
  },
  'text-justify': {
    section: 'expandedCss',
    default: 'auto',
    path: 'textJustify' as const,
    typographic: true,
  },
  'text-orientation': {
    section: 'expandedCss',
    default: 'mixed',
    path: 'textOrientation' as const,
    typographic: true,
  },
  'text-overflow': {
    section: 'typography',
    path: 'textOverflow' as const,
    default: 'clip',
  },
  'text-rendering': {
    section: 'expandedCss',
    default: 'auto',
    path: 'textRendering' as const,
    typographic: true,
  },
  'text-shadow': {
    section: 'typography',
    path: 'textShadows' as const,
    typographic: true,
    multi: true,
    allowSwatches: true,
    supportedVariableTypes: ['length'],
  },
  'text-size-adjust': {
    section: 'expandedCss',
    default: 'autoForSmartphoneBrowsersSupportingInflation',
    path: 'textSizeAdjust' as const,
    typographic: true,
  },
  'text-spacing-trim': {
    section: 'expandedCss',
    default: 'normal',
    path: 'textSpacingTrim' as const,
    typographic: true,
  },
  'text-transform': {
    section: 'typography',
    path: 'textTransform' as const,
    typographic: true,
    default: 'none',
  },
  'text-underline-offset': {
    section: 'typography',
    path: 'textUnderlineOffset' as const,
    supportedVariableTypes: ['length', 'percentage'],
    typographic: true,
    default: 'auto',
  },
  'text-underline-position': {
    section: 'typography',
    path: 'textUnderlinePosition' as const,
    typographic: true,
    default: 'auto',
  },
  'text-wrap': {
    section: 'expandedCss',
    computed: ['text-wrap-mode', 'text-wrap-style'],
    default: 'wrap',
    path: 'textWrap' as const,
    typographic: true,
  },
  'text-wrap-mode': {
    section: 'expandedCss',
    default: 'wrap',
    path: 'textWrapMode' as const,
    typographic: true,
    computedFrom: ['text-wrap'],
  },
  'text-wrap-style': {
    section: 'expandedCss',
    default: 'auto',
    path: 'textWrapStyle' as const,
    typographic: true,
    computedFrom: ['text-wrap'],
  },
  'timeline-scope': {
    section: 'expandedCss',
    default: 'none',
    path: 'timelineScope' as const,
  },
  top: {
    section: 'position',
    path: 'top' as const,
    default: 'auto',
    units: ['%', 'px', 'auto'],
    defaultUnit: 'px',
    min: -9999,
    max: 9999,
    dimensional: true,
    supportedVariableTypes: ['length', 'percentage'],
    computedFrom: ['inset'],
  },
  'touch-action': {
    section: 'expandedCss',
    default: 'auto',
    path: 'touchAction' as const,
  },
  transform: {
    section: 'transitionsAndTransforms',
    path: 'transforms' as const,
    multi: true,
  },
  'transform-box': {
    section: 'expandedCss',
    default: 'view-box',
    path: 'transformBox' as const,
  },
  'transform-origin': {
    section: 'transitionsAndTransforms',
    path: 'transformOrigin' as const,
    default: '50% 50% 0',
    units: ['%', 'px'],
  },
  'transform-style': {
    section: 'transitionsAndTransforms',
    path: 'transformStyle' as const,
    default: 'flat',
  },
  transition: {
    section: 'transitionsAndTransforms',
    computed: [
      'transition-delay',
      'transition-duration',
      'transition-property',
      'transition-timing-function',
      'transition-behavior',
    ],
    path: 'transitions' as const,
    multi: true,
  },
  'transition-behavior': {
    section: 'expandedCss',
    default: 'normal',
    path: 'transitionBehavior' as const,
    computedFrom: ['transition'],
  },
  'transition-delay': {
    section: 'transitionsAndTransforms',
    path: 'transitions' as const,
    combo: 'delay',
    computedFrom: ['transition'],
  },
  'transition-duration': {
    section: 'transitionsAndTransforms',
    path: 'transitions' as const,
    combo: 'duration',
    computedFrom: ['transition'],
  },
  'transition-property': {
    section: 'transitionsAndTransforms',
    path: 'transitions' as const,
    combo: 'property',
    computedFrom: ['transition'],
  },
  'transition-timing-function': {
    section: 'transitionsAndTransforms',
    path: 'transitions' as const,
    combo: 'timing',
    computedFrom: ['transition'],
  },
  translate: {
    section: 'expandedCss',
    default: 'none',
    path: 'translate' as const,
  },
  'unicode-bidi': {
    section: 'expandedCss',
    default: 'normal',
    path: 'unicodeBidi' as const,
  },
  'user-select': {
    section: 'expandedCss',
    default: 'auto',
    path: 'userSelect' as const,
  },
  'vector-effect': {
    section: 'expandedCss',
    default: 'none',
    path: 'vectorEffect' as const,
  },
  'vertical-align': {
    section: 'layout',
    path: 'verticalAlign' as const,
    default: 'baseline',
  },
  'view-timeline': {
    section: 'expandedCss',
    computed: ['view-timeline-name', 'view-timeline-axis'],
    default: 'none block',
    path: 'viewTimeline' as const,
  },
  'view-timeline-axis': {
    section: 'expandedCss',
    default: 'block',
    path: 'viewTimelineAxis' as const,
    computedFrom: ['view-timeline'],
  },
  'view-timeline-inset': {
    section: 'expandedCss',
    default: 'auto',
    path: 'viewTimelineInset' as const,
  },
  'view-timeline-name': {
    section: 'expandedCss',
    default: 'none',
    path: 'viewTimelineName' as const,
    computedFrom: ['view-timeline'],
  },
  'view-transition-name': {
    section: 'expandedCss',
    default: 'none',
    path: 'viewTransitionName' as const,
  },
  visibility: {
    section: 'layout',
    path: 'visibility' as const,
    default: 'visible',
    typographic: true,
  },
  'white-space': {
    section: 'layout',
    path: 'whiteSpace' as const,
    typographic: true,
    default: 'normal',
  },
  'white-space-collapse': {
    section: 'expandedCss',
    default: 'collapse',
    path: 'whiteSpaceCollapse' as const,
    typographic: true,
  },
  widows: {
    section: 'expandedCss',
    default: '2',
    path: 'widows' as const,
    typographic: true,
  },
  width: {
    section: 'sizing',
    path: 'width' as const,
    default: 'auto',
    units: ['%', 'px', 'auto'],
    defaultUnit: 'px',
    min: 0,
    max: 9999,
    placeholder: 'Auto',
    dimensional: true,
    supportedVariableTypes: ['length', 'percentage'],
  },
  'will-change': {
    section: 'expandedCss',
    default: 'auto',
    path: 'willChange' as const,
  },
  'word-break': {
    section: 'typography',
    path: 'wordBreak' as const,
    typographic: true,
    default: 'normal',
  },
  'word-spacing': {
    section: 'expandedCss',
    default: 'normal',
    path: 'wordSpacing' as const,
    typographic: true,
  },
  'word-wrap': {
    section: 'expandedCss',
    default: 'normal',
    path: 'wordWrap' as const,
    typographic: true,
  },
  'writing-mode': {
    section: 'expandedCss',
    default: 'horizontal-tb',
    path: 'writingMode' as const,
    typographic: true,
  },
  x: {
    section: 'expandedCss',
    default: '0',
    path: 'x' as const,
  },
  y: {
    section: 'expandedCss',
    default: '0',
    path: 'y' as const,
  },
  'z-index': {
    section: 'position',
    path: 'zIndex' as const,
    supportedVariableTypes: ['number'],
  },
  zoom: {
    section: 'expandedCss',
    default: 'normal',
    path: 'zoom' as const,
  },
};

/**
 * CSS_FUNCTIONS is a list of all the valid css functions that are supported in Webflow.
 */
export const CSS_FUNCTIONS = [
  'anchor',
  'anchor-size',
  'attr',
  'blur',
  'brightness',
  'calc',
  'circle',
  'clamp',
  'color-mix',
  'conic-gradient',
  'contrast',
  'counter',
  'counters',
  'cross-fade',
  'drop-shadow',
  'element',
  'ellipse',
  'env',
  'fit-content',
  'grayscale',
  'hsl',
  'hsla',
  'hue-rotate',
  'image',
  'image-set',
  'inset',
  'invert',
  'leader',
  'linear-gradient',
  'matrix',
  'matrix3d',
  'max',
  'min',
  'minmax',
  'oklab',
  'oklch',
  'opacity',
  'paint',
  'path',
  'perspective',
  'polygon',
  'radial-gradient',
  'ray',
  'repeating-linear-gradient',
  'repeating-radial-gradient',
  'rgb',
  'rgba',
  'rotate',
  'rotate3d',
  'rotateX',
  'rotateY',
  'rotateZ',
  'saturate',
  'scale',
  'scale3d',
  'scaleX',
  'scaleY',
  'scaleZ',
  'scroll',
  'sepia',
  'skew',
  'skewX',
  'skewY',
  'target-counter',
  'target-counters',
  'target-text',
  'translate',
  'translate3d',
  'translateX',
  'translateY',
  'translateZ',
  'var',
  'view',
] as const;

/**
 * PATH_TO_CSS_PROPERTY is a mapping of StyleStore paths to the corresponding
 * css property. Useful if you have the style store path (typically in camelcase),
 * and need to get the css property (e.g. in kebab-case).
 */
export const PATH_TO_CSS_PROPERTY = {
  WebkitTextFillColor: '-webkit-text-fill-color' as const,
  textStrokeColor: '-webkit-text-stroke-color' as const,
  textStrokeWidth: '-webkit-text-stroke-width' as const,
  accentColor: 'accent-color' as const,
  alignContent: 'align-content' as const,
  alignItems: 'align-items' as const,
  alignSelf: 'align-self' as const,
  alignTracks: 'align-tracks' as const,
  all: 'all' as const,
  anchorName: 'anchor-name' as const,
  anchorScope: 'anchor-scope' as const,
  animation: 'animation' as const,
  animationComposition: 'animation-composition' as const,
  animationDelay: 'animation-delay' as const,
  animationDirection: 'animation-direction' as const,
  animationDuration: 'animation-duration' as const,
  animationFillMode: 'animation-fill-mode' as const,
  animationIterationCount: 'animation-iteration-count' as const,
  animationName: 'animation-name' as const,
  animationPlayState: 'animation-play-state' as const,
  animationRange: 'animation-range' as const,
  animationRangeEnd: 'animation-range-end' as const,
  animationRangeStart: 'animation-range-start' as const,
  animationTimingFunction: 'animation-timing-function' as const,
  animationTimeline: 'animation-timeline' as const,
  appearance: 'appearance' as const,
  aspectRatio: 'aspect-ratio' as const,
  azimuth: 'azimuth' as const,
  backdropFilters: 'backdrop-filter' as const,
  backfaceVisibility: 'backface-visibility' as const,
  backgroundImages: 'background' as const,
  backgroundBlendMode: 'background-blend-mode' as const,
  backgroundClip: 'background-clip' as const,
  backgroundColor: 'background-color' as const,
  backgroundOrigin: 'background-origin' as const,
  backgroundPositionX: 'background-position-x' as const,
  backgroundPositionY: 'background-position-y' as const,
  blockSize: 'block-size' as const,
  border: 'border' as const,
  borderBlock: 'border-block' as const,
  borderBlockColor: 'border-block-color' as const,
  borderBlockStyle: 'border-block-style' as const,
  borderBlockWidth: 'border-block-width' as const,
  borderBlockEnd: 'border-block-end' as const,
  borderBlockEndColor: 'border-block-end-color' as const,
  borderBlockEndStyle: 'border-block-end-style' as const,
  borderBlockEndWidth: 'border-block-end-width' as const,
  borderBlockStart: 'border-block-start' as const,
  borderBlockStartColor: 'border-block-start-color' as const,
  borderBlockStartStyle: 'border-block-start-style' as const,
  borderBlockStartWidth: 'border-block-start-width' as const,
  borderBottom: 'border-bottom' as const,
  borderBottomColor: 'border-bottom-color' as const,
  borderRadiusBottomLeft: 'border-bottom-left-radius' as const,
  borderRadiusBottomRight: 'border-bottom-right-radius' as const,
  borderBottomStyle: 'border-bottom-style' as const,
  borderBottomWidth: 'border-bottom-width' as const,
  borderCollapse: 'border-collapse' as const,
  borderColor: 'border-color' as const,
  borderEndEndRadius: 'border-end-end-radius' as const,
  borderEndStartRadius: 'border-end-start-radius' as const,
  borderImage: 'border-image' as const,
  borderImageOutset: 'border-image-outset' as const,
  borderImageRepeat: 'border-image-repeat' as const,
  borderImageSlice: 'border-image-slice' as const,
  borderImageSource: 'border-image-source' as const,
  borderImageWidth: 'border-image-width' as const,
  borderInline: 'border-inline' as const,
  borderInlineEnd: 'border-inline-end' as const,
  borderInlineColor: 'border-inline-color' as const,
  borderInlineStyle: 'border-inline-style' as const,
  borderInlineWidth: 'border-inline-width' as const,
  borderInlineEndColor: 'border-inline-end-color' as const,
  borderInlineEndStyle: 'border-inline-end-style' as const,
  borderInlineEndWidth: 'border-inline-end-width' as const,
  borderInlineStart: 'border-inline-start' as const,
  borderInlineStartColor: 'border-inline-start-color' as const,
  borderInlineStartStyle: 'border-inline-start-style' as const,
  borderInlineStartWidth: 'border-inline-start-width' as const,
  borderLeft: 'border-left' as const,
  borderLeftColor: 'border-left-color' as const,
  borderLeftStyle: 'border-left-style' as const,
  borderLeftWidth: 'border-left-width' as const,
  borderRadius: 'border-radius' as const,
  borderRight: 'border-right' as const,
  borderRightColor: 'border-right-color' as const,
  borderRightStyle: 'border-right-style' as const,
  borderRightWidth: 'border-right-width' as const,
  borderSpacing: 'border-spacing' as const,
  borderStartEndRadius: 'border-start-end-radius' as const,
  borderStartStartRadius: 'border-start-start-radius' as const,
  borderStyle: 'border-style' as const,
  borderTop: 'border-top' as const,
  borderTopColor: 'border-top-color' as const,
  borderRadiusTopLeft: 'border-top-left-radius' as const,
  borderRadiusTopRight: 'border-top-right-radius' as const,
  borderTopStyle: 'border-top-style' as const,
  borderTopWidth: 'border-top-width' as const,
  borderWidth: 'border-width' as const,
  bottom: 'bottom' as const,
  boxAlign: 'box-align' as const,
  boxDecorationBreak: 'box-decoration-break' as const,
  boxDirection: 'box-direction' as const,
  boxFlex: 'box-flex' as const,
  boxFlexGroup: 'box-flex-group' as const,
  boxLines: 'box-lines' as const,
  boxOrdinalGroup: 'box-ordinal-group' as const,
  boxOrient: 'box-orient' as const,
  boxPack: 'box-pack' as const,
  boxShadows: 'box-shadow' as const,
  boxSizing: 'box-sizing' as const,
  breakAfter: 'break-after' as const,
  breakBefore: 'break-before' as const,
  breakInside: 'break-inside' as const,
  captionSide: 'caption-side' as const,
  caret: 'caret' as const,
  caretColor: 'caret-color' as const,
  caretShape: 'caret-shape' as const,
  clear: 'clear' as const,
  clip: 'clip' as const,
  clipPath: 'clip-path' as const,
  clipRule: 'clip-rule' as const,
  fontColor: 'color' as const,
  colorInterpolationFilters: 'color-interpolation-filters' as const,
  colorScheme: 'color-scheme' as const,
  columnCount: 'column-count' as const,
  columnFill: 'column-fill' as const,
  columnGap: 'column-gap' as const,
  columnRule: 'column-rule' as const,
  columnRuleColor: 'column-rule-color' as const,
  columnRuleStyle: 'column-rule-style' as const,
  columnRuleWidth: 'column-rule-width' as const,
  columnSpan: 'column-span' as const,
  columnWidth: 'column-width' as const,
  columns: 'columns' as const,
  contain: 'contain' as const,
  containIntrinsicSize: 'contain-intrinsic-size' as const,
  containIntrinsicBlockSize: 'contain-intrinsic-block-size' as const,
  containIntrinsicHeight: 'contain-intrinsic-height' as const,
  containIntrinsicInlineSize: 'contain-intrinsic-inline-size' as const,
  containIntrinsicWidth: 'contain-intrinsic-width' as const,
  container: 'container' as const,
  containerName: 'container-name' as const,
  containerType: 'container-type' as const,
  content: 'content' as const,
  contentVisibility: 'content-visibility' as const,
  counterIncrement: 'counter-increment' as const,
  counterReset: 'counter-reset' as const,
  counterSet: 'counter-set' as const,
  cursor: 'cursor' as const,
  d: 'd' as const,
  cx: 'cx' as const,
  cy: 'cy' as const,
  direction: 'direction' as const,
  display: 'display' as const,
  dominantBaseline: 'dominant-baseline' as const,
  emptyCells: 'empty-cells' as const,
  fieldSizing: 'field-sizing' as const,
  fill: 'fill' as const,
  fillOpacity: 'fill-opacity' as const,
  fillRule: 'fill-rule' as const,
  filters: 'filter' as const,
  flex: 'flex' as const,
  flexBasis: 'flex-basis' as const,
  flexDirection: 'flex-direction' as const,
  flexFlow: 'flex-flow' as const,
  flexGrow: 'flex-grow' as const,
  flexShrink: 'flex-shrink' as const,
  flexWrap: 'flex-wrap' as const,
  float: 'float' as const,
  font: 'font' as const,
  fontFamily: 'font-family' as const,
  fontFeatureSettings: 'font-feature-settings' as const,
  fontKerning: 'font-kerning' as const,
  fontLanguageOverride: 'font-language-override' as const,
  fontOpticalSizing: 'font-optical-sizing' as const,
  fontPalette: 'font-palette' as const,
  fontVariationSettings: 'font-variation-settings' as const,
  fontSize: 'font-size' as const,
  fontSizeAdjust: 'font-size-adjust' as const,
  fontSmooth: 'font-smooth' as const,
  fontStretch: 'font-stretch' as const,
  fontStyle: 'font-style' as const,
  fontSynthesis: 'font-synthesis' as const,
  fontSynthesisPosition: 'font-synthesis-position' as const,
  fontSynthesisSmallCaps: 'font-synthesis-small-caps' as const,
  fontSynthesisStyle: 'font-synthesis-style' as const,
  fontSynthesisWeight: 'font-synthesis-weight' as const,
  fontVariant: 'font-variant' as const,
  fontVariantAlternates: 'font-variant-alternates' as const,
  fontVariantCaps: 'font-variant-caps' as const,
  fontVariantEastAsian: 'font-variant-east-asian' as const,
  fontVariantLigatures: 'font-variant-ligatures' as const,
  fontVariantNumeric: 'font-variant-numeric' as const,
  fontVariantPosition: 'font-variant-position' as const,
  fontWeight: 'font-weight' as const,
  forcedColorAdjust: 'forced-color-adjust' as const,
  gap: 'gap' as const,
  grid: 'grid' as const,
  gridArea: 'grid-area' as const,
  gridAutoColumns: 'grid-auto-columns' as const,
  gridAutoFlow: 'grid-auto-flow' as const,
  gridAutoRows: 'grid-auto-rows' as const,
  gridColumn: 'grid-column' as const,
  gridColumnEnd: 'grid-column-end' as const,
  gridColumnGap: 'grid-column-gap' as const,
  gridColumnStart: 'grid-column-start' as const,
  gridGap: 'grid-gap' as const,
  gridRow: 'grid-row' as const,
  gridRowEnd: 'grid-row-end' as const,
  gridRowGap: 'grid-row-gap' as const,
  gridRowStart: 'grid-row-start' as const,
  gridTemplate: 'grid-template' as const,
  gridTemplateAreas: 'grid-template-areas' as const,
  gridTemplateColumns: 'grid-template-columns' as const,
  gridTemplateRows: 'grid-template-rows' as const,
  hangingPunctuation: 'hanging-punctuation' as const,
  height: 'height' as const,
  hyphenateCharacter: 'hyphenate-character' as const,
  hyphenateLimitChars: 'hyphenate-limit-chars' as const,
  hyphens: 'hyphens' as const,
  imageOrientation: 'image-orientation' as const,
  imageRendering: 'image-rendering' as const,
  imageResolution: 'image-resolution' as const,
  imeMode: 'ime-mode' as const,
  initialLetter: 'initial-letter' as const,
  initialLetterAlign: 'initial-letter-align' as const,
  inlineSize: 'inline-size' as const,
  inputSecurity: 'input-security' as const,
  inset: 'inset' as const,
  insetBlock: 'inset-block' as const,
  insetBlockEnd: 'inset-block-end' as const,
  insetBlockStart: 'inset-block-start' as const,
  insetInline: 'inset-inline' as const,
  insetInlineEnd: 'inset-inline-end' as const,
  insetInlineStart: 'inset-inline-start' as const,
  interpolateSize: 'interpolate-size' as const,
  isolation: 'isolation' as const,
  justifyContent: 'justify-content' as const,
  justifyItems: 'justify-items' as const,
  justifySelf: 'justify-self' as const,
  justifyTracks: 'justify-tracks' as const,
  left: 'left' as const,
  letterSpacing: 'letter-spacing' as const,
  lineBreak: 'line-break' as const,
  lineHeight: 'line-height' as const,
  lineHeightStep: 'line-height-step' as const,
  listStyle: 'list-style' as const,
  listStyleImage: 'list-style-image' as const,
  listStylePosition: 'list-style-position' as const,
  listStyleType: 'list-style-type' as const,
  margin: 'margin' as const,
  marginBlock: 'margin-block' as const,
  marginBlockEnd: 'margin-block-end' as const,
  marginBlockStart: 'margin-block-start' as const,
  marginBottom: 'margin-bottom' as const,
  marginInline: 'margin-inline' as const,
  marginInlineEnd: 'margin-inline-end' as const,
  marginInlineStart: 'margin-inline-start' as const,
  marginLeft: 'margin-left' as const,
  marginRight: 'margin-right' as const,
  marginTop: 'margin-top' as const,
  marginTrim: 'margin-trim' as const,
  marker: 'marker' as const,
  markerEnd: 'marker-end' as const,
  markerMid: 'marker-mid' as const,
  markerStart: 'marker-start' as const,
  mask: 'mask' as const,
  maskBorder: 'mask-border' as const,
  maskBorderMode: 'mask-border-mode' as const,
  maskBorderOutset: 'mask-border-outset' as const,
  maskBorderRepeat: 'mask-border-repeat' as const,
  maskBorderSlice: 'mask-border-slice' as const,
  maskBorderSource: 'mask-border-source' as const,
  maskBorderWidth: 'mask-border-width' as const,
  maskClip: 'mask-clip' as const,
  maskComposite: 'mask-composite' as const,
  maskImage: 'mask-image' as const,
  maskMode: 'mask-mode' as const,
  maskOrigin: 'mask-origin' as const,
  maskPosition: 'mask-position' as const,
  maskRepeat: 'mask-repeat' as const,
  maskSize: 'mask-size' as const,
  maskType: 'mask-type' as const,
  masonryAutoFlow: 'masonry-auto-flow' as const,
  mathDepth: 'math-depth' as const,
  mathShift: 'math-shift' as const,
  mathStyle: 'math-style' as const,
  maxBlockSize: 'max-block-size' as const,
  maxHeight: 'max-height' as const,
  maxInlineSize: 'max-inline-size' as const,
  maxLines: 'max-lines' as const,
  maxWidth: 'max-width' as const,
  minBlockSize: 'min-block-size' as const,
  minHeight: 'min-height' as const,
  minInlineSize: 'min-inline-size' as const,
  minWidth: 'min-width' as const,
  mixBlendMode: 'mix-blend-mode' as const,
  objectFit: 'object-fit' as const,
  objectPosition: 'object-position' as const,
  offset: 'offset' as const,
  offsetAnchor: 'offset-anchor' as const,
  offsetDistance: 'offset-distance' as const,
  offsetPath: 'offset-path' as const,
  offsetPosition: 'offset-position' as const,
  offsetRotate: 'offset-rotate' as const,
  opacity: 'opacity' as const,
  order: 'order' as const,
  orphans: 'orphans' as const,
  outline: 'outline' as const,
  outlineColor: 'outline-color' as const,
  outlineOffset: 'outline-offset' as const,
  outlineStyle: 'outline-style' as const,
  outlineWidth: 'outline-width' as const,
  overflow: 'overflow' as const,
  overflowAnchor: 'overflow-anchor' as const,
  overflowBlock: 'overflow-block' as const,
  overflowClipBox: 'overflow-clip-box' as const,
  overflowClipMargin: 'overflow-clip-margin' as const,
  overflowInline: 'overflow-inline' as const,
  overflowWrap: 'overflow-wrap' as const,
  overflowX: 'overflow-x' as const,
  overflowY: 'overflow-y' as const,
  overlay: 'overlay' as const,
  overscrollBehavior: 'overscroll-behavior' as const,
  overscrollBehaviorBlock: 'overscroll-behavior-block' as const,
  overscrollBehaviorInline: 'overscroll-behavior-inline' as const,
  overscrollBehaviorX: 'overscroll-behavior-x' as const,
  overscrollBehaviorY: 'overscroll-behavior-y' as const,
  padding: 'padding' as const,
  paddingBlock: 'padding-block' as const,
  paddingBlockEnd: 'padding-block-end' as const,
  paddingBlockStart: 'padding-block-start' as const,
  paddingBottom: 'padding-bottom' as const,
  paddingInline: 'padding-inline' as const,
  paddingInlineEnd: 'padding-inline-end' as const,
  paddingInlineStart: 'padding-inline-start' as const,
  paddingLeft: 'padding-left' as const,
  paddingRight: 'padding-right' as const,
  paddingTop: 'padding-top' as const,
  page: 'page' as const,
  pageBreakAfter: 'page-break-after' as const,
  pageBreakBefore: 'page-break-before' as const,
  pageBreakInside: 'page-break-inside' as const,
  paintOrder: 'paint-order' as const,
  perspective: 'perspective' as const,
  perspectiveOrigin: 'perspective-origin' as const,
  placeContent: 'place-content' as const,
  placeItems: 'place-items' as const,
  placeSelf: 'place-self' as const,
  pointerEvents: 'pointer-events' as const,
  position: 'position' as const,
  positionAnchor: 'position-anchor' as const,
  positionArea: 'position-area' as const,
  positionTry: 'position-try' as const,
  positionTryFallbacks: 'position-try-fallbacks' as const,
  positionTryOrder: 'position-try-order' as const,
  positionVisibility: 'position-visibility' as const,
  printColorAdjust: 'print-color-adjust' as const,
  quotes: 'quotes' as const,
  r: 'r' as const,
  resize: 'resize' as const,
  right: 'right' as const,
  rotate: 'rotate' as const,
  rowGap: 'row-gap' as const,
  rubyAlign: 'ruby-align' as const,
  rubyMerge: 'ruby-merge' as const,
  rubyPosition: 'ruby-position' as const,
  rx: 'rx' as const,
  ry: 'ry' as const,
  scale: 'scale' as const,
  scrollbarColor: 'scrollbar-color' as const,
  scrollbarGutter: 'scrollbar-gutter' as const,
  scrollbarWidth: 'scrollbar-width' as const,
  scrollBehavior: 'scroll-behavior' as const,
  scrollMargin: 'scroll-margin' as const,
  scrollMarginBlock: 'scroll-margin-block' as const,
  scrollMarginBlockStart: 'scroll-margin-block-start' as const,
  scrollMarginBlockEnd: 'scroll-margin-block-end' as const,
  scrollMarginBottom: 'scroll-margin-bottom' as const,
  scrollMarginInline: 'scroll-margin-inline' as const,
  scrollMarginInlineStart: 'scroll-margin-inline-start' as const,
  scrollMarginInlineEnd: 'scroll-margin-inline-end' as const,
  scrollMarginLeft: 'scroll-margin-left' as const,
  scrollMarginRight: 'scroll-margin-right' as const,
  scrollMarginTop: 'scroll-margin-top' as const,
  scrollPadding: 'scroll-padding' as const,
  scrollPaddingBlock: 'scroll-padding-block' as const,
  scrollPaddingBlockStart: 'scroll-padding-block-start' as const,
  scrollPaddingBlockEnd: 'scroll-padding-block-end' as const,
  scrollPaddingBottom: 'scroll-padding-bottom' as const,
  scrollPaddingInline: 'scroll-padding-inline' as const,
  scrollPaddingInlineStart: 'scroll-padding-inline-start' as const,
  scrollPaddingInlineEnd: 'scroll-padding-inline-end' as const,
  scrollPaddingLeft: 'scroll-padding-left' as const,
  scrollPaddingRight: 'scroll-padding-right' as const,
  scrollPaddingTop: 'scroll-padding-top' as const,
  scrollSnapAlign: 'scroll-snap-align' as const,
  scrollSnapCoordinate: 'scroll-snap-coordinate' as const,
  scrollSnapDestination: 'scroll-snap-destination' as const,
  scrollSnapPointsX: 'scroll-snap-points-x' as const,
  scrollSnapPointsY: 'scroll-snap-points-y' as const,
  scrollSnapStop: 'scroll-snap-stop' as const,
  scrollSnapType: 'scroll-snap-type' as const,
  scrollSnapTypeX: 'scroll-snap-type-x' as const,
  scrollSnapTypeY: 'scroll-snap-type-y' as const,
  scrollTimeline: 'scroll-timeline' as const,
  scrollTimelineAxis: 'scroll-timeline-axis' as const,
  scrollTimelineName: 'scroll-timeline-name' as const,
  shapeImageThreshold: 'shape-image-threshold' as const,
  shapeMargin: 'shape-margin' as const,
  shapeOutside: 'shape-outside' as const,
  shapeRendering: 'shape-rendering' as const,
  stroke: 'stroke' as const,
  strokeDasharray: 'stroke-dasharray' as const,
  strokeDashoffset: 'stroke-dashoffset' as const,
  strokeLinecap: 'stroke-linecap' as const,
  strokeLinejoin: 'stroke-linejoin' as const,
  strokeMiterlimit: 'stroke-miterlimit' as const,
  strokeOpacity: 'stroke-opacity' as const,
  strokeWidth: 'stroke-width' as const,
  tabSize: 'tab-size' as const,
  tableLayout: 'table-layout' as const,
  textAlign: 'text-align' as const,
  textAlignLast: 'text-align-last' as const,
  textAnchor: 'text-anchor' as const,
  textCombineUpright: 'text-combine-upright' as const,
  textDecoration: 'text-decoration' as const,
  textDecorationColor: 'text-decoration-color' as const,
  textDecorationLine: 'text-decoration-line' as const,
  textDecorationSkip: 'text-decoration-skip' as const,
  textDecorationSkipInk: 'text-decoration-skip-ink' as const,
  textDecorationStyle: 'text-decoration-style' as const,
  textDecorationThickness: 'text-decoration-thickness' as const,
  textEmphasis: 'text-emphasis' as const,
  textEmphasisColor: 'text-emphasis-color' as const,
  textEmphasisPosition: 'text-emphasis-position' as const,
  textEmphasisStyle: 'text-emphasis-style' as const,
  textIndent: 'text-indent' as const,
  textJustify: 'text-justify' as const,
  textOrientation: 'text-orientation' as const,
  textOverflow: 'text-overflow' as const,
  textRendering: 'text-rendering' as const,
  textShadows: 'text-shadow' as const,
  textSizeAdjust: 'text-size-adjust' as const,
  textSpacingTrim: 'text-spacing-trim' as const,
  textTransform: 'text-transform' as const,
  textUnderlineOffset: 'text-underline-offset' as const,
  textUnderlinePosition: 'text-underline-position' as const,
  textWrap: 'text-wrap' as const,
  textWrapMode: 'text-wrap-mode' as const,
  textWrapStyle: 'text-wrap-style' as const,
  timelineScope: 'timeline-scope' as const,
  top: 'top' as const,
  touchAction: 'touch-action' as const,
  transforms: 'transform' as const,
  transformBox: 'transform-box' as const,
  transformOrigin: 'transform-origin' as const,
  transformStyle: 'transform-style' as const,
  transitions: 'transition' as const,
  transitionBehavior: 'transition-behavior' as const,
  translate: 'translate' as const,
  unicodeBidi: 'unicode-bidi' as const,
  userSelect: 'user-select' as const,
  vectorEffect: 'vector-effect' as const,
  verticalAlign: 'vertical-align' as const,
  viewTimeline: 'view-timeline' as const,
  viewTimelineAxis: 'view-timeline-axis' as const,
  viewTimelineInset: 'view-timeline-inset' as const,
  viewTimelineName: 'view-timeline-name' as const,
  viewTransitionName: 'view-transition-name' as const,
  visibility: 'visibility' as const,
  whiteSpace: 'white-space' as const,
  whiteSpaceCollapse: 'white-space-collapse' as const,
  widows: 'widows' as const,
  width: 'width' as const,
  willChange: 'will-change' as const,
  wordBreak: 'word-break' as const,
  wordSpacing: 'word-spacing' as const,
  wordWrap: 'word-wrap' as const,
  writingMode: 'writing-mode' as const,
  x: 'x' as const,
  y: 'y' as const,
  zIndex: 'z-index' as const,
  zoom: 'zoom' as const,
};
